import { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import api, { API } from "../lib/api";
import Header from "../components/Header";
import BallotWizard from "../components/BallotWizard";
import AuditTrailPanel from "../components/AuditTrailPanel";
import SuperAdminsPanel from "../components/SuperAdminsPanel";
import SessionMonitor from "../components/SessionMonitor";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { Checkbox } from "../components/ui/checkbox";
import ResendWelcomeDialog from "../components/ResendWelcomeDialog";
import {
    Plus, Trash, UsersThree, Cube, ChartLine, Lightning, ShieldCheck,
    PaperPlaneTilt, Stop, IdentificationBadge, X,
    DownloadSimple, UploadSimple, FileXls, MapPin, Warning,
    PencilSimple, Trash as TrashIcon,
    EnvelopeSimple, Eye, EyeSlash, CaretDown, CaretUp,
    Database, Clock, Calendar, CheckCircle, FloppyDisk, Lock, Timer,
    CircleNotch, WarningCircle, SquaresFour, ListBullets, ArrowRight, ClockCounterClockwise,
    Archive, CaretRight
} from "@phosphor-icons/react";
import { toast } from "sonner";

function fmtDt(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
    } catch { return iso; }
}

// Convert ISO timestamp to value usable by <input type="datetime-local"> (local time, no TZ).
function isoToLocalInput(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Returns { open_now, before_start, after_end } based on current local time vs the election window.
function windowState(e) {
    const now = Date.now();
    const starts = e.starts_at ? new Date(e.starts_at).getTime() : null;
    const ends = e.ends_at ? new Date(e.ends_at).getTime() : null;
    const before = !!(starts && now < starts);
    const after = !!(ends && now > ends);
    return {
        open_now: e.status === "active" && !before && !after,
        before_start: before,
        after_end: after,
    };
}

function StatTile({ label, value, icon: Icon, accent = false, testid }) {
    return (
        <Card className={`px-3 py-2 border-slate-200 ${accent ? "bg-civic-600 text-white border-civic-600" : "bg-white"}`} data-testid={testid}>
            <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                    <div className={`overline text-[10px] ${accent ? "text-civic-100" : "text-slate-500"}`}>{label}</div>
                    <div className={`font-heading font-black text-xl leading-none tracking-tighter mt-0.5 ${accent ? "" : "text-slate-900"}`}>{value}</div>
                </div>
                <Icon size={16} weight="duotone" className={accent ? "text-civic-100 shrink-0" : "text-civic-600 shrink-0"} />
            </div>
        </Card>
    );
}

export default function AdminDashboard() {
    const [stats, setStats] = useState(null);
    const [elections, setElections] = useState([]);
    const [voters, setVoters] = useState([]);
    // Tab state driven by ?tab= so we can deep-link / restore after impersonation
    const initialTab = (() => {
        try {
            const t = new URLSearchParams(window.location.search).get("tab");
            return ["elections", "voters", "audit", "admins", "settings", "sales", "danger"].includes(t) ? t : "elections";
        } catch { return "elections"; }
    })();
    const [activeTab, setActiveTab] = useState(initialTab);
    const [showArchivedElections, setShowArchivedElections] = useState(
        () => localStorage.getItem("admin_elections_show_archived") === "1"
    );

    const loadAll = async (opts = {}) => {
        const includeArchived = opts.includeArchived ?? showArchivedElections;
        try {
            const [s, e, v] = await Promise.all([
                api.get("/admin/stats"),
                api.get("/admin/elections", { params: includeArchived ? { include_archived: true } : {} }),
                api.get("/admin/voters"),
            ]);
            setStats(s.data); setElections(e.data); setVoters(v.data);
        } catch (err) {
            toast.error("Failed to load admin data");
        }
    };
    useEffect(() => { loadAll({ includeArchived: showArchivedElections }); }, [showArchivedElections]); // eslint-disable-line react-hooks/exhaustive-deps
    const toggleShowArchived = () => {
        setShowArchivedElections((prev) => {
            const next = !prev;
            localStorage.setItem("admin_elections_show_archived", next ? "1" : "0");
            return next;
        });
    };

    return (
        <div className="min-h-screen bg-slate-50">
            <Header />
            <div className="max-w-7xl mx-auto px-6 py-10">
                <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
                    <div>
                        <div className="overline text-civic-600">Election authority console</div>
                        <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter mt-1 text-slate-900">Admin Dashboard</h1>
                        <p className="text-sm text-slate-600 mt-2">Configure elections, manage voter rolls, and verify chain integrity.</p>
                    </div>
                    <SessionMonitor />
                </div>

                {stats && (
                    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-10" data-testid="admin-stats">
                        <StatTile testid="stat-elections" label="Elections" value={stats.total_elections} icon={ChartLine} />
                        <StatTile testid="stat-active" label="Active" value={stats.active_elections} icon={Lightning} accent />
                        <StatTile testid="stat-voters" label="Registered Voters" value={stats.total_voters} icon={UsersThree} />
                        <StatTile testid="stat-votes" label="Votes on Chain" value={stats.total_votes} icon={Cube} />
                        <StatTile testid="stat-chain" label={stats.chain_valid ? "Chain Valid" : "Chain Broken"} value={`${stats.chain_length}`} icon={ShieldCheck} />
                    </div>
                )}

                <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-2">
                    <TabsList className="bg-white border border-slate-200">
                        <TabsTrigger value="elections" data-testid="tab-elections">Elections</TabsTrigger>
                        <TabsTrigger value="voters" data-testid="tab-voters">Voter Roll</TabsTrigger>
                        <TabsTrigger value="audit" data-testid="tab-audit">Audit Trail</TabsTrigger>
                        <TabsTrigger value="admins" data-testid="tab-admins">Super Admins</TabsTrigger>
                        <TabsTrigger value="settings" data-testid="tab-settings">Site Settings</TabsTrigger>
                        <TabsTrigger value="sales" data-testid="tab-sales">Sales / Licensing</TabsTrigger>
                        <TabsTrigger value="danger" data-testid="tab-danger">Danger Zone</TabsTrigger>
                    </TabsList>

                    <TabsContent value="elections" className="mt-6">
                        <ElectionsPanel
                            elections={elections}
                            reload={loadAll}
                            showArchived={showArchivedElections}
                            onToggleArchived={toggleShowArchived}
                        />
                    </TabsContent>
                    <TabsContent value="voters" className="mt-6">
                        <VotersPanel voters={voters} reload={loadAll} />
                    </TabsContent>
                    <TabsContent value="audit" className="mt-6">
                        <AuditTrailPanel />
                    </TabsContent>
                    <TabsContent value="admins" className="mt-6">
                        <SuperAdminsPanel />
                    </TabsContent>
                    <TabsContent value="settings" className="mt-6">
                        <SettingsPanel />
                    </TabsContent>
                    <TabsContent value="sales" className="mt-6">
                        <SalesLicensingPanel />
                    </TabsContent>
                    <TabsContent value="danger" className="mt-6">
                        <DangerZone reload={loadAll} />
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
}

function ElectionsPanel({ elections, reload, showArchived, onToggleArchived }) {
    const [open, setOpen] = useState(false);
    const [viewMode, setViewMode] = useState(() => localStorage.getItem("admin_elections_view") || "grid"); // "grid" | "list"
    const [collapsed, setCollapsed] = useState(() => {
        try {
            const raw = localStorage.getItem("admin_elections_collapsed");
            return new Set(raw ? JSON.parse(raw) : []);
        } catch { return new Set(); }
    });
    const persistCollapsed = (next) => {
        try { localStorage.setItem("admin_elections_collapsed", JSON.stringify([...next])); } catch { /* quota exceeded — ignore */ }
        return next;
    };
    const toggleView = (m) => { setViewMode(m); localStorage.setItem("admin_elections_view", m); };
    const toggleCollapsed = (id) => setCollapsed((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return persistCollapsed(next);
    });
    const collapseAll = () => setCollapsed(persistCollapsed(new Set(elections.map((e) => e.id))));
    const expandAll = () => setCollapsed(persistCollapsed(new Set()));

    // Sortable list-view columns (Election / Authority / Status).
    const [sortBy, setSortBy] = useState({ key: "title", dir: "asc" });
    const toggleSort = (key) => setSortBy((prev) => (
        prev.key !== key ? { key, dir: "asc" } : { key, dir: prev.dir === "asc" ? "desc" : "asc" }
    ));
    // Status ordering that reads naturally: Active first, then Draft, then Closed.
    const STATUS_ORDER = { active: 0, draft: 1, closed: 2 };
    const sortedElections = useMemo(() => {
        const arr = [...elections];
        const { key, dir } = sortBy;
        const mult = dir === "asc" ? 1 : -1;
        const val = (e) => {
            if (key === "status") return STATUS_ORDER[(e.status || "").toLowerCase()] ?? 99;
            return (e?.[key] ?? "").toString().toLowerCase();
        };
        arr.sort((a, b) => {
            const av = val(a); const bv = val(b);
            if (av < bv) return -1 * mult;
            if (av > bv) return 1 * mult;
            return 0;
        });
        return arr;
    }, [elections, sortBy]);

    const ListSortHeader = ({ k, label, className = "" }) => {
        const active = sortBy.key === k;
        const Arrow = active && sortBy.dir === "desc" ? CaretDown : CaretUp;
        return (
            <button
                type="button"
                onClick={() => toggleSort(k)}
                className={`inline-flex items-center gap-1 hover:text-slate-900 transition-colors ${active ? "text-civic-700" : ""} ${className}`}
                data-testid={`election-sort-${k}`}
                aria-sort={active ? (sortBy.dir === "asc" ? "ascending" : "descending") : "none"}
            >
                <span>{label}</span>
                <Arrow size={10} weight="bold" className={active ? "opacity-100" : "opacity-30"} />
            </button>
        );
    };

    const [editing, setEditing] = useState(null); // election object when editing, null when creating
    const [title, setTitle] = useState("");
    const [desc, setDesc] = useState("");
    const [cands, setCands] = useState([{ id: null, name: "", party: "" }]);
    const [startsAt, setStartsAt] = useState("");
    const [endsAt, setEndsAt] = useState("");
    const [authority, setAuthority] = useState("Denton County Elections");
    const [precincts, setPrecincts] = useState("9912");
    const [secureElection, setSecureElection] = useState(false);
    const [autoClose, setAutoClose] = useState(false);
    const [adminEmails, setAdminEmails] = useState("");
    const [deleting, setDeleting] = useState(null); // election to delete
    const [deleteChallenge, setDeleteChallenge] = useState(""); // type-to-confirm text
    const [qs, setQs] = useState([]); // ballot questions: {id, title, text, type, options(string for multi)}
    const [questionFileMode, setQuestionFileMode] = useState("replace"); // "replace" | "append"
    const qFileRef = useRef(null);
    const [previewElection, setPreviewElection] = useState(null); // election whose ballot we're previewing
    const [emailHistoryElection, setEmailHistoryElection] = useState(null); // election whose email log we're viewing


    const addCand = () => setCands([...cands, { id: null, name: "", party: "" }]);
    const upCand = (i, k, v) => {
        const n = [...cands]; n[i][k] = v; setCands(n);
    };
    const delCand = (i) => setCands(cands.filter((_, idx) => idx !== i));

    const reset = () => {
        setEditing(null);
        setTitle(""); setDesc(""); setCands([{ id: null, name: "", party: "" }]);
        setQs([]);
        setStartsAt(""); setEndsAt("");
        setAuthority("Denton County Elections"); setPrecincts("9912");
        setSecureElection(false);
        setAdminEmails("");
        setAutoClose(false);
    };

    const addQ = () => setQs((arr) => [...arr, { id: null, title: "", text: "", type: "yes_no", options: "", max_selections: 1 }]);
    const upQ = (i, k, v) => setQs((arr) => { const n = [...arr]; n[i] = { ...n[i], [k]: v }; return n; });
    const delQ = (i) => setQs((arr) => arr.filter((_, idx) => idx !== i));

    const openEdit = (e) => {
        setEditing(e);
        setTitle(e.title || "");
        setDesc(e.description || "");
        setCands(
            (e.candidates && e.candidates.length > 0)
                ? e.candidates.map((c) => ({ id: c.id, name: c.name || "", party: c.party || "" }))
                : [{ id: null, name: "", party: "" }]
        );
        setQs((e.questions || []).map((q) => ({
            id: q.id, title: q.title || "", text: q.text || "",
            type: q.type, options: q.type === "multi" ? (q.options || []).join(", ") : "",
            max_selections: q.type === "multi" ? Math.max(1, parseInt(q.max_selections || 1, 10) || 1) : 1,
        })));
        setStartsAt(isoToLocalInput(e.starts_at));
        setEndsAt(isoToLocalInput(e.ends_at));
        setAuthority(e.authority || "Denton County Elections");
        setPrecincts((e.precincts || []).join(", "));
        setSecureElection(!!e.secure_election);
        setAdminEmails((e.admin_emails || []).join(", "));
        setAutoClose(!!e.auto_close_on_full_turnout);
        setOpen(true);
    };

    /**
     * Pre-fill the "Draft new election" form from an existing election.
     * Everything is copied EXCEPT:
     *   • the title (reset to "NEW ELECTION" so the user is nudged to rename)
     *   • start / end times (cleared — you almost never want to reuse a past window)
     *   • question ids (dropped so the backend inserts fresh rows instead of
     *     silently PATCH-ing the source election)
     */
    const cloneFrom = (electionId) => {
        if (!electionId) return;
        const src = elections.find((el) => el.id === electionId);
        if (!src) { toast.error("Election not found"); return; }
        setEditing(null);              // still a CREATE, not an edit
        setTitle("NEW ELECTION");
        setDesc(src.description || "");
        setCands(
            (src.candidates && src.candidates.length > 0)
                ? src.candidates.map((c) => ({ id: null, name: c.name || "", party: c.party || "" }))
                : [{ id: null, name: "", party: "" }]
        );
        setQs((src.questions || []).map((q) => ({
            id: null,
            title: q.title || "",
            text: q.text || "",
            type: q.type,
            options: q.type === "multi" ? (q.options || []).join(", ") : "",
            max_selections: q.type === "multi" ? Math.max(1, parseInt(q.max_selections || 1, 10) || 1) : 1,
        })));
        setStartsAt("");   // force the user to pick a new window
        setEndsAt("");
        setAuthority(src.authority || "Denton County Elections");
        setPrecincts((src.precincts || []).join(", "));
        setSecureElection(!!src.secure_election);
        setAdminEmails((src.admin_emails || []).join(", "));
        setAutoClose(!!src.auto_close_on_full_turnout);
        toast.success(`Cloned from "${src.title}" — set your new title, dates, and click Create draft.`);
    };

    const submit = async () => {
        const valid = cands.filter((c) => c.name.trim());
        const validQs = qs.filter((q) => q.title.trim() && q.text.trim());
        if (!title.trim() || (valid.length < 2 && validQs.length === 0)) {
            toast.error("Need title and either ≥2 candidates or ≥1 ballot question");
            return;
        }
        const precinctList = precincts.split(",").map((p) => p.trim()).filter(Boolean);
        const questionsPayload = validQs.map((q) => {
            const out = { id: q.id || null, title: q.title.trim(), text: q.text.trim(), type: q.type, options: [], max_selections: 1 };
            if (q.type === "multi") {
                out.options = q.options.split(",").map((o) => o.trim()).filter(Boolean);
                const n = parseInt(q.max_selections, 10);
                out.max_selections = Number.isFinite(n) && n >= 1 ? n : 1;
            }
            return out;
        });
        const payload = {
            title: title.trim(),
            description: desc.trim() || null,
            candidates: valid.map((c) => ({
                id: c.id || null, name: c.name.trim(), party: c.party.trim() || null,
            })),
            questions: questionsPayload,
            starts_at: startsAt ? new Date(startsAt).toISOString() : null,
            ends_at: endsAt ? new Date(endsAt).toISOString() : null,
            authority: authority.trim() || "Denton County Elections",
            precincts: precinctList,
            secure_election: !!secureElection,
            auto_close_on_full_turnout: !!autoClose,
            admin_emails: adminEmails.split(/[,\s;]+/).map((s) => s.trim()).filter(Boolean),
        };
        try {
            if (editing) {
                await api.put(`/admin/elections/${editing.id}`, payload);
                toast.success("Election updated");
            } else {
                await api.post("/admin/elections", payload);
                toast.success("Election created (draft)");
            }
            setOpen(false); reset();
            reload();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to save");
        }
    };

    const downloadFile = async (path, filename) => {
        try {
            const res = await api.get(path, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url; a.download = filename; document.body.appendChild(a); a.click();
            a.remove(); window.URL.revokeObjectURL(url);
        } catch { toast.error("Download failed"); }
    };

    const onUploadQs = async (ev) => {
        const file = ev.target.files?.[0];
        if (!file || !editing) return;
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post(
                `/admin/elections/${editing.id}/questions/import?mode=${questionFileMode}`,
                fd, { headers: { "Content-Type": "multipart/form-data" } }
            );
            toast.success(`Imported ${data.created_count} questions · ${data.skipped_count} skipped`);
            // Reload election data into local state
            const fresh = await api.get("/admin/elections");
            const refreshed = fresh.data.find((e) => e.id === editing.id);
            if (refreshed) {
                setQs((refreshed.questions || []).map((q) => ({
                    id: q.id, title: q.title || "", text: q.text || "", type: q.type,
                    options: q.type === "multi" ? (q.options || []).join(", ") : "",
                    max_selections: q.type === "multi" ? Math.max(1, parseInt(q.max_selections || 1, 10) || 1) : 1,
                })));
            }
            reload();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Import failed");
        }
        if (qFileRef.current) qFileRef.current.value = "";
    };

    const setStatus = async (id, status) => {
        try {
            await api.patch(`/admin/elections/${id}/status`, { status });
            toast.success(`Election ${status}`);
            reload();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    const [emailBusy, setEmailBusy] = useState(null); // `${id}:${kind}` while in flight
    const [emailPreview, setEmailPreview] = useState(null); // { election, kind, subject, html, recipient_count, sample_recipient }
    const [customEmail, setCustomEmail] = useState(null); // { election, subject, message }
    const openEmailPreview = async (election, kind, note = "", resendAll = false) => {
        setEmailBusy(`${election.id}:${kind}`);
        try {
            const { data } = await api.get(`/admin/elections/${election.id}/email/${kind}/preview`, { params: { note, resend_all: resendAll } });
            setEmailPreview({ election, ...data, note });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not load preview");
        }
        setEmailBusy(null);
    };
    const refreshEmailPreview = async (note) => {
        if (!emailPreview) return;
        const { election, kind } = emailPreview;
        try {
            const { data } = await api.get(`/admin/elections/${election.id}/email/${kind}/preview`, { params: { note } });
            setEmailPreview((cur) => (cur && cur.kind === kind ? { ...cur, html: data.html } : cur));
        } catch { /* keep last good preview */ }
    };
    const confirmAndSendEmail = async () => {
        if (!emailPreview) return;
        const { election, kind, note, resend_all } = emailPreview;
        const labels = { welcome: "Welcome", reminder: "Reminder", results: "Results" };
        setEmailBusy(`${election.id}:${kind}`);
        try {
            const { data } = await api.post(`/admin/elections/${election.id}/email/${kind}`, { note: note || "", resend_all: !!resend_all });
            const summary = data.total_eligible === undefined
                ? (data.detail || "Nothing to send")
                : data.total_eligible === 0
                ? "No eligible voters with email on file"
                : `${data.sent} sent${data.failed ? ` · ${data.failed} failed` : ""}${data.bounced ? ` (${data.bounced} bounced — see Voter Roll)` : ""} of ${data.total_eligible} eligible`;
            toast.success(`${labels[kind]} email — ${summary}`);
            setEmailPreview(null);
        } catch (e) {
            toast.error(e.response?.data?.detail || `Could not send ${labels[kind]} email`);
        }
        setEmailBusy(null);
    };

    const confirmDelete = async () => {
        if (!deleting) return;
        try {
            const { data } = await api.delete(`/admin/elections/${deleting.id}`);
            toast.success(`Election deleted · ${data.votes_removed} votes removed · chain re-linked`);
            setDeleting(null);
            setDeleteChallenge("");
            reload();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Delete failed");
        }
    };

    const toggleArchive = async (e) => {
        const nowArchived = !e.archived;
        const verb = nowArchived ? "Archive" : "Unarchive";
        if (!window.confirm(`${verb} "${e.title}"?${nowArchived ? "\n\nArchived elections are hidden from every public and voter surface. You can restore them later from the \"Show archived\" toggle." : ""}`)) return;
        try {
            await api.post(`/admin/elections/${e.id}/${nowArchived ? "archive" : "unarchive"}`);
            toast.success(`${verb}d · ${e.title}`);
            reload();
        } catch (err) {
            toast.error(err.response?.data?.detail || `${verb} failed`);
        }
    };

    const toggleResultsHidden = async (e) => {
        const next = !e.results_hidden;
        try {
            await api.put(`/admin/elections/${e.id}`, { results_hidden: next });
            toast.success(next
                ? `Results hidden · "${e.title}" tallies are no longer visible to the public`
                : `Results revealed · "${e.title}" tallies are now public`);
            reload();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not toggle results visibility");
        }
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                <h2 className="font-heading font-bold text-xl text-slate-900">Elections</h2>
                <div className="flex items-center gap-2">
                    {/* View mode toggle */}
                    <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5" role="tablist" aria-label="View mode">
                        <button
                            type="button"
                            onClick={() => toggleView("grid")}
                            className={`h-7 px-2 rounded text-[11px] hash-mono flex items-center gap-1 transition-colors ${viewMode === "grid" ? "bg-civic-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}
                            data-testid="elections-view-grid"
                            title="Grid view"
                        >
                            <SquaresFour size={12} weight="fill" /> Grid
                        </button>
                        <button
                            type="button"
                            onClick={() => toggleView("list")}
                            className={`h-7 px-2 rounded text-[11px] hash-mono flex items-center gap-1 transition-colors ${viewMode === "list" ? "bg-civic-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}
                            data-testid="elections-view-list"
                            title="List view"
                        >
                            <ListBullets size={12} weight="fill" /> List
                        </button>
                    </div>
                    {viewMode === "grid" && elections.length > 0 && (
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => (collapsed.size === elections.length ? expandAll() : collapseAll())}
                            className="h-8 text-[11px] hash-mono"
                            data-testid="elections-collapse-all"
                        >
                            {collapsed.size === elections.length ? "Expand all" : "Collapse all"}
                        </Button>
                    )}
                    <Button
                        type="button"
                        variant={showArchived ? "default" : "outline"}
                        size="sm"
                        onClick={onToggleArchived}
                        className={`h-8 text-[11px] hash-mono ${showArchived ? "bg-slate-700 hover:bg-slate-800 text-white" : ""}`}
                        data-testid="elections-show-archived-toggle"
                        title={showArchived ? "Currently showing archived. Click to hide them again." : "Include archived elections in this list"}
                    >
                        <Archive size={12} weight="fill" className="mr-1" />
                        {showArchived ? "Showing archived" : "Show archived"}
                    </Button>
                    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
                    <DialogTrigger asChild>
                        <Button className="bg-civic-600 hover:bg-civic-700 text-white" data-testid="open-create-election-btn">
                            <Plus size={16} className="mr-2" weight="bold" /> New Election
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="election-form-dialog">
                        <DialogHeader>
                            <DialogTitle className="font-heading text-2xl">
                                {editing ? "Edit election" : "Draft new election"}
                            </DialogTitle>
                            <DialogDescription>
                                {editing
                                    ? "Update the question, candidates, time window, and eligibility."
                                    : "Define the question, candidates, time window, and eligibility."}
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                            {!editing && elections.length > 0 && (
                                <div className="rounded-md border border-civic-100 bg-civic-50/60 px-3 py-2.5" data-testid="clone-from-strip">
                                    <Label htmlFor="clone-from-select" className="overline text-civic-700 block mb-1">
                                        Clone from an existing election (optional)
                                    </Label>
                                    <div className="flex items-center gap-2">
                                        <select
                                            id="clone-from-select"
                                            defaultValue=""
                                            onChange={(ev) => { const v = ev.target.value; if (v) cloneFrom(v); ev.target.value = ""; }}
                                            className="flex-1 h-9 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700 hash-mono"
                                            data-testid="clone-from-select"
                                        >
                                            <option value="">— Pick an election to duplicate its ballot, precincts & settings —</option>
                                            {[...elections]
                                                .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))
                                                .map((el) => (
                                                    <option key={el.id} value={el.id}>
                                                        {el.title} {el.status === "closed" ? "· closed" : el.status === "active" ? "· active" : "· draft"}
                                                    </option>
                                                ))
                                            }
                                        </select>
                                    </div>
                                    <p className="text-[10px] text-slate-500 mt-1">Copies candidates, ballot questions, precincts, authority, and toggles. Title resets to <span className="hash-mono">NEW ELECTION</span>; dates clear so you set a fresh window.</p>
                                </div>
                            )}
                            <div>
                                <Label className="overline text-slate-500">Title</Label>
                                <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="2026 General Assembly" data-testid="election-title-input" />
                            </div>
                            <div>
                                <Label className="overline text-slate-500">Description</Label>
                                <Textarea rows={2} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Optional description" data-testid="election-desc-input" />
                            </div>
                            <div className="grid sm:grid-cols-2 gap-3">
                                <div>
                                    <Label className="overline text-slate-500">Polls open</Label>
                                    <Input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} data-testid="election-starts-input" />
                                </div>
                                <div>
                                    <Label className="overline text-slate-500">Polls close</Label>
                                    <Input type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} data-testid="election-ends-input" />
                                </div>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-3">
                                <div>
                                    <Label className="overline text-slate-500">Election authority</Label>
                                    <Input value={authority} onChange={(e) => setAuthority(e.target.value)} placeholder="Denton County Elections" data-testid="election-authority-input" />
                                </div>
                                <div>
                                    <Label className="overline text-slate-500">Precincts (comma-separated)</Label>
                                    <Input value={precincts} onChange={(e) => setPrecincts(e.target.value)} placeholder="9912, 9913" className="font-mono" data-testid="election-precincts-input" />
                                </div>
                            </div>

                            <div>
                                <Label className="overline text-slate-500">Election administrator email(s) <span className="text-slate-400 normal-case">(comma-separated)</span></Label>
                                <Input value={adminEmails} onChange={(e) => setAdminEmails(e.target.value)} placeholder="clerk@county.gov, ada@county.gov" className="font-mono" data-testid="election-admin-emails-input" />
                                <p className="text-[11px] text-slate-500 mt-1">Contacts responsible for this election — used for admin notifications and audit follow-up.</p>
                            </div>

                            {/* Secure Election toggle */}
                            <label className={`flex items-start gap-3 rounded-md border px-3 py-2.5 cursor-pointer select-none transition-colors ${secureElection ? "bg-rose-50 border-rose-200" : "bg-slate-50 border-slate-200 hover:border-slate-300"}`} data-testid="secure-election-toggle-label">
                                <Checkbox checked={secureElection} onCheckedChange={(v) => setSecureElection(!!v)} className="mt-0.5" data-testid="election-secure-checkbox" />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5">
                                        <Lock size={13} weight="fill" className={secureElection ? "text-rose-600" : "text-slate-500"} />
                                        <span className={`font-heading font-bold text-sm ${secureElection ? "text-rose-900" : "text-slate-900"}`}>Secure Election</span>
                                        {secureElection && <Badge className="bg-rose-600 text-white border-0 text-[10px] hash-mono h-4 px-1.5 py-0">REQUIRES PHOTO</Badge>}
                                    </div>
                                    <p className={`text-[11px] mt-0.5 leading-snug ${secureElection ? "text-rose-800" : "text-slate-600"}`}>
                                        When enabled, voters must take a photo of their face before final submission. Photos are stored on the public "Who voted" log alongside their name &amp; precinct for transparency.
                                    </p>
                                </div>
                            </label>
                            {/* Auto-close on 100% turnout toggle */}
                            <label className={`flex items-start gap-3 rounded-md border px-3 py-2.5 cursor-pointer select-none transition-colors ${autoClose ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200 hover:border-slate-300"}`} data-testid="auto-close-toggle-label">
                                <Checkbox checked={autoClose} onCheckedChange={(v) => setAutoClose(!!v)} className="mt-0.5" data-testid="election-autoclose-checkbox" />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5">
                                        <Timer size={13} weight="fill" className={autoClose ? "text-emerald-600" : "text-slate-500"} />
                                        <span className={`font-heading font-bold text-sm ${autoClose ? "text-emerald-900" : "text-slate-900"}`}>Auto-close at 100% turnout</span>
                                        {autoClose && <Badge className="bg-emerald-600 text-white border-0 text-[10px] hash-mono h-4 px-1.5 py-0">EARLY CLOSE</Badge>}
                                    </div>
                                    <p className={`text-[11px] mt-0.5 leading-snug ${autoClose ? "text-emerald-800" : "text-slate-600"}`}>
                                        When enabled, the election closes automatically the moment every eligible voter has cast their ballot — no need to wait for the scheduled end time.
                                    </p>
                                </div>
                            </label>
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <Label className="overline text-slate-500">Candidates <span className="text-slate-400 normal-case">(optional)</span></Label>
                                    <Button size="sm" variant="outline" onClick={addCand} data-testid="add-candidate-btn"><Plus size={14} className="mr-1" /> Add</Button>
                                </div>
                                <div className="space-y-2">
                                    {cands.map((c, i) => (
                                        <div key={c.id || `new-${i}`} className="flex gap-2 items-center" data-testid={`candidate-row-${i}`}>
                                            <Input placeholder="Name" value={c.name} onChange={(e) => upCand(i, "name", e.target.value)} className="flex-1" data-testid={`candidate-name-${i}`} />
                                            <Input placeholder="Party (optional)" value={c.party} onChange={(e) => upCand(i, "party", e.target.value)} className="flex-1" data-testid={`candidate-party-${i}`} />
                                            <Button size="icon" variant="ghost" onClick={() => delCand(i)} disabled={cands.length === 1} data-testid={`remove-candidate-${i}`}>
                                                <X size={16} />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Ballot questions */}
                            <div className="border-t border-slate-200 pt-4">
                                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                                    <Label className="overline text-slate-500">Ballot questions</Label>
                                    <div className="flex items-center gap-2">
                                        <input ref={qFileRef} type="file" accept=".xlsx,.xlsm" onChange={onUploadQs} className="hidden" data-testid="questions-import-input" />
                                        <Button size="sm" variant="outline"
                                            onClick={() => downloadFile("/admin/questions/template", "ballot_questions_template.xlsx")}
                                            data-testid="questions-template-btn"
                                        ><FileXls size={14} className="mr-1" /> Template</Button>
                                        {editing && (
                                            <>
                                                <Button size="sm" variant="outline"
                                                    onClick={() => downloadFile(`/admin/elections/${editing.id}/questions/export`, "questions.xlsx")}
                                                    data-testid="questions-export-btn"
                                                ><DownloadSimple size={14} className="mr-1" /> Export</Button>
                                                <select value={questionFileMode} onChange={(e) => setQuestionFileMode(e.target.value)}
                                                    className="text-xs h-8 rounded-md border border-slate-300 px-2"
                                                    data-testid="questions-import-mode"
                                                >
                                                    <option value="replace">Replace</option>
                                                    <option value="append">Append</option>
                                                </select>
                                                <Button size="sm" variant="outline"
                                                    onClick={() => qFileRef.current?.click()}
                                                    data-testid="questions-import-btn"
                                                ><UploadSimple size={14} className="mr-1" /> Import</Button>
                                            </>
                                        )}
                                        <Button size="sm" variant="outline" onClick={addQ} data-testid="add-question-btn"><Plus size={14} className="mr-1" /> Add</Button>
                                    </div>
                                </div>
                                {!editing && qs.length === 0 && (
                                    <p className="text-xs text-slate-500 -mt-1 mb-2">Tip: Excel import is available after the election is created (Save first, then re-open this dialog to import).</p>
                                )}
                                <div className="space-y-3">
                                    {qs.map((q, i) => (
                                        <Card key={q.id || `nq-${i}`} className="p-3 bg-slate-50 border-slate-200" data-testid={`question-row-${i}`}>
                                            <div className="flex items-start gap-2">
                                                <div className="flex-1 space-y-2">
                                                    <Input placeholder="Question title" value={q.title} onChange={(e) => upQ(i, "title", e.target.value)} data-testid={`q-title-${i}`} />
                                                    <Textarea rows={2} placeholder="Question text shown to voter" value={q.text} onChange={(e) => upQ(i, "text", e.target.value)} data-testid={`q-text-${i}`} />
                                                    <div className="grid sm:grid-cols-2 gap-2">
                                                        <Select value={q.type} onValueChange={(v) => upQ(i, "type", v)}>
                                                            <SelectTrigger data-testid={`q-type-${i}`}><SelectValue /></SelectTrigger>
                                                            <SelectContent>
                                                                <SelectItem value="for_against">For / Against</SelectItem>
                                                                <SelectItem value="yes_no">Yes / No</SelectItem>
                                                                <SelectItem value="multi">Multi-choice</SelectItem>
                                                                <SelectItem value="rating">Rating 1–10</SelectItem>
                                                                <SelectItem value="text_response">Text Response</SelectItem>
                                                            </SelectContent>
                                                        </Select>
                                                        {q.type === "multi" ? (
                                                            <Input placeholder="Options (comma-separated)" value={q.options} onChange={(e) => upQ(i, "options", e.target.value)} data-testid={`q-options-${i}`} />
                                                        ) : (
                                                            <div className="text-xs text-slate-500 self-center hash-mono">
                                                                {q.type === "for_against" ? "Options: FOR · AGAINST"
                                                                : q.type === "yes_no" ? "Options: YES · NO"
                                                                : q.type === "rating" ? "Voter picks a number 1–10"
                                                                : q.type === "text_response" ? "Voter types a short answer (≤500 chars)"
                                                                : ""}
                                                            </div>
                                                        )}
                                                    </div>
                                                    {q.type === "multi" && (
                                                        <div className="flex items-center gap-2 pt-1">
                                                            <Label className="overline text-slate-500 shrink-0">Max selections</Label>
                                                            <Input
                                                                type="number"
                                                                min={1}
                                                                max={99}
                                                                value={q.max_selections || 1}
                                                                onChange={(e) => upQ(i, "max_selections", parseInt(e.target.value, 10) || 1)}
                                                                className="w-20 hash-mono"
                                                                data-testid={`q-maxselect-${i}`}
                                                            />
                                                            <span className="text-xs text-slate-500">
                                                                {(q.max_selections || 1) > 1 ? `Voter can pick up to ${q.max_selections} options` : "Voter picks exactly one (default)"}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                                <Button size="icon" variant="ghost" onClick={() => delQ(i)} data-testid={`q-remove-${i}`}>
                                                    <X size={16} />
                                                </Button>
                                            </div>
                                        </Card>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => { setOpen(false); reset(); }}>Cancel</Button>
                            <Button onClick={submit} className="bg-civic-600 hover:bg-civic-700 text-white" data-testid="election-submit-btn">
                                {editing ? "Save changes" : "Create draft"}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
                </div>
            </div>

            {elections.length === 0 ? (
                <Card className="p-10 text-center bg-white border-slate-200" data-testid="no-elections-admin">
                    <Cube size={32} weight="duotone" className="text-civic-600 mx-auto mb-3" />
                    <div className="font-heading font-bold text-lg text-slate-900">No elections yet</div>
                    <p className="text-sm text-slate-500 mt-2">Create your first election to start anchoring votes on the chain.</p>
                </Card>
            ) : viewMode === "list" ? (
                <div className="rounded-md border border-slate-200 bg-white overflow-hidden" data-testid="admin-elections-list-view">
                    <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                        <div className="col-span-3"><ListSortHeader k="title" label="Election" /></div>
                        <div className="col-span-2"><ListSortHeader k="authority" label="Authority" /></div>
                        <div className="col-span-2"><ListSortHeader k="status" label="Status" /></div>
                        <div className="col-span-1 text-right">Votes</div>
                        <div className="col-span-2">Window</div>
                        <div className="col-span-2 text-right">Actions</div>
                    </div>
                    <div className="divide-y divide-slate-100">
                        {sortedElections.map((e) => (
                            <div key={e.id} className={`grid grid-cols-12 gap-2 px-3 py-2 items-center text-sm hover:bg-slate-50 ${e.archived ? "opacity-70 bg-slate-50/50" : ""}`} data-testid={`admin-election-row-${e.id}`}>
                                <div className="col-span-3 min-w-0">
                                    <div className="font-semibold text-slate-900 truncate flex items-center gap-1.5">
                                        {e.archived && <Archive size={11} weight="fill" className="text-slate-500 shrink-0" title="Archived" />}
                                        <span className="truncate">{e.title}</span>
                                    </div>
                                    <div className="hash-mono text-[10px] text-slate-500 truncate">pct {(e.precincts || []).join(", ") || "—"}</div>
                                </div>
                                <div className="col-span-2 min-w-0 text-slate-700 truncate" data-testid={`admin-list-authority-${e.id}`}>
                                    {e.authority || <span className="italic text-slate-400">—</span>}
                                </div>
                                <div className="col-span-2"><StatusBadge status={e.status} resultsHref={e.status === "closed" ? `/results/${e.id}` : undefined} testId={`admin-list-status-${e.id}`} /></div>
                                <div className="col-span-1 text-right hash-mono text-xs">
                                    <span className="font-semibold text-slate-900">{e.votes_count || 0}</span>
                                    {typeof e.eligible_voters === "number" && <span className="text-slate-400"> / {e.eligible_voters}</span>}
                                </div>
                                <div className="col-span-2 hash-mono text-[10px] text-slate-500 truncate">
                                    {e.starts_at ? new Date(e.starts_at).toLocaleDateString() : "—"} → {e.ends_at ? new Date(e.ends_at).toLocaleDateString() : "—"}
                                </div>
                                <div className="col-span-2 flex items-center justify-end gap-1">
                                    <Button size="sm" variant="outline" className="h-7 text-[10px] hash-mono px-2" onClick={() => openEdit(e)} data-testid={`edit-${e.id}`}>Edit</Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className={`h-7 px-2 ${e.archived ? "text-amber-700 border-amber-300 hover:bg-amber-50" : "text-slate-600 border-slate-200 hover:bg-slate-50"}`}
                                        onClick={() => toggleArchive(e)}
                                        data-testid={`archive-${e.id}`}
                                        title={e.archived ? "Restore from archive" : "Archive · hide everywhere but this admin list"}
                                    >
                                        <Archive size={11} weight={e.archived ? "fill" : "regular"} />
                                    </Button>
                                    <Button size="sm" variant="outline" className="h-7 px-2 text-rose-700 border-rose-200 hover:bg-rose-50" onClick={() => setDeleting(e)} data-testid={`delete-${e.id}`}><Trash size={11} /></Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <div className="grid lg:grid-cols-2 gap-4 items-stretch" data-testid="admin-elections-list">
                    {elections.map((e) => (
                        <Card key={e.id} className={`bg-white border-slate-200 flex flex-col ${collapsed.has(e.id) ? "p-3 h-auto" : "p-5 h-full"} ${e.archived ? "opacity-70 ring-1 ring-slate-300 border-dashed" : ""}`} data-testid={`admin-election-${e.id}`}>
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                                        <StatusBadge status={e.status} />
                                        {e.archived && (
                                            <Badge className="bg-slate-200 text-slate-700 border border-slate-300 hash-mono text-[10px] h-5 px-1.5 py-0" data-testid={`archived-badge-${e.id}`}>
                                                <Archive size={9} weight="fill" className="mr-1" /> ARCHIVED
                                            </Badge>
                                        )}
                                        {(() => {
                                            const w = windowState(e);
                                            if (e.status !== "active") return null;
                                            if (w.open_now) return <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200">Open now</Badge>;
                                            if (w.before_start) return <Badge className="bg-amber-100 text-amber-900 border border-amber-200">Pending · opens later</Badge>;
                                            if (w.after_end) return <Badge className="bg-slate-200 text-slate-700 border border-slate-300">Past close time</Badge>;
                                            return null;
                                        })()}
                                        {e.secure_election && (
                                            <Badge className="bg-rose-100 text-rose-800 border border-rose-200 hash-mono text-[10px] h-5 px-1.5 py-0" data-testid={`secure-badge-${e.id}`}>
                                                <Lock size={9} weight="fill" className="mr-1" /> SECURE
                                            </Badge>
                                        )}
                                        {e.results_hidden && (
                                            <Badge className="bg-amber-100 text-amber-900 border border-amber-200 hash-mono text-[10px] h-5 px-1.5 py-0" data-testid={`results-hidden-badge-${e.id}`}>
                                                <EyeSlash size={9} weight="fill" className="mr-1" /> RESULTS HIDDEN
                                            </Badge>
                                        )}
                                        <span className="hash-mono text-xs text-slate-400">id: {e.id.slice(0, 8)}</span>
                                    </div>
                                    <h3 className="font-heading font-bold text-lg text-slate-900">{e.title}</h3>
                                    {e.description && <p className="text-sm text-slate-600 mt-1">{e.description}</p>}
                                </div>
                                <div className="text-right flex flex-col items-end gap-2">
                                    <div data-testid={`votes-${e.id}`}>
                                        <div className="overline text-slate-500">Votes</div>
                                        <div className="hash-mono text-2xl text-civic-700 font-semibold leading-none">
                                            <span data-testid={`votes-count-${e.id}`}>{e.votes_count || 0}</span>
                                            {typeof e.eligible_voters === "number" && (
                                                <span className="text-slate-400 font-normal">/<span data-testid={`votes-eligible-${e.id}`}>{e.eligible_voters}</span></span>
                                            )}
                                        </div>
                                        {typeof e.eligible_voters === "number" && e.eligible_voters > 0 && (
                                            <div className="mt-0.5 hash-mono text-[10px] text-slate-500" data-testid={`votes-turnout-${e.id}`}>
                                                {Math.round(((e.votes_count || 0) / e.eligible_voters) * 100)}% turnout
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex gap-1">
                                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => toggleCollapsed(e.id)} aria-label={collapsed.has(e.id) ? "Expand" : "Collapse"} title={collapsed.has(e.id) ? "Expand" : "Collapse"} data-testid={`collapse-${e.id}`}>
                                            <CaretDown size={16} className={`text-slate-500 transition-transform ${collapsed.has(e.id) ? "-rotate-90" : ""}`} />
                                        </Button>
                                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(e)} data-testid={`edit-${e.id}`}>
                                            <PencilSimple size={16} className="text-slate-600" />
                                        </Button>
                                        <Button
                                            size="icon"
                                            variant="ghost"
                                            className="h-8 w-8 hover:bg-slate-100"
                                            onClick={() => toggleArchive(e)}
                                            data-testid={`archive-${e.id}`}
                                            title={e.archived ? "Restore from archive" : "Archive · hide everywhere but the admin list"}
                                            aria-label={e.archived ? "Unarchive election" : "Archive election"}
                                        >
                                            <Archive size={16} weight={e.archived ? "fill" : "regular"} className={e.archived ? "text-amber-600" : "text-slate-500"} />
                                        </Button>
                                        <Button size="icon" variant="ghost" className="h-8 w-8 hover:bg-red-50" onClick={() => setDeleting(e)} data-testid={`delete-${e.id}`}>
                                            <TrashIcon size={16} className="text-red-600" />
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            {!collapsed.has(e.id) && (
                            <>
                            <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                                <div>
                                    <div className="overline text-slate-500">Authority</div>
                                    <div className="text-slate-800 mt-0.5">{e.authority || <span className="text-slate-400 italic">—</span>}</div>
                                </div>
                                <div>
                                    <div className="overline text-slate-500">Precincts</div>
                                    <div className="flex flex-wrap gap-1 mt-0.5">
                                        {(e.precincts || []).length === 0
                                            ? <span className="text-slate-400 italic">all</span>
                                            : e.precincts.map((p) => (
                                                <Badge key={p} variant="outline" className="border-civic-200 text-civic-700 hash-mono">{p}</Badge>
                                            ))
                                        }
                                    </div>
                                </div>
                                <div>
                                    <div className="overline text-slate-500">Polls open <span className="text-slate-400 normal-case">(local)</span></div>
                                    <div className="hash-mono text-slate-700 mt-0.5">{fmtDt(e.starts_at)}</div>
                                </div>
                                <div>
                                    <div className="overline text-slate-500">Polls close <span className="text-slate-400 normal-case">(local)</span></div>
                                    <div className="hash-mono text-slate-700 mt-0.5">{fmtDt(e.ends_at)}</div>
                                </div>
                            </div>

                            <div className="mt-4 flex flex-wrap gap-1.5 min-h-[28px]">
                                {e.candidates?.map((c) => (
                                    <Badge key={c.id} variant="outline" className="border-slate-300 text-slate-700">{c.name}{c.party ? ` · ${c.party}` : ""}</Badge>
                                ))}
                            </div>

                            {(e.admin_emails || []).length > 0 && (
                                <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid={`admin-emails-${e.id}`}>
                                    <span className="overline text-slate-500 shrink-0">Admin</span>
                                    {e.admin_emails.slice(0, 3).map((em) => (
                                        <Badge key={em} variant="outline" className="border-slate-300 text-slate-600 hash-mono text-[10px] font-normal">{em}</Badge>
                                    ))}
                                    {e.admin_emails.length > 3 && (
                                        <span className="text-[10px] hash-mono text-slate-500">+{e.admin_emails.length - 3} more</span>
                                    )}
                                </div>
                            )}

                            <div className="mt-auto pt-4 flex flex-wrap items-center gap-2">
                                {e.status === "draft" && (
                                    <Button size="sm" onClick={() => setStatus(e.id, "active")} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`activate-${e.id}`}>
                                        <PaperPlaneTilt size={14} className="mr-1.5" weight="bold" /> Activate
                                    </Button>
                                )}
                                {e.status === "active" && (
                                    <Button size="sm" onClick={() => setStatus(e.id, "closed")} variant="outline" className="border-slate-300" data-testid={`close-${e.id}`}>
                                        <Stop size={14} className="mr-1.5" weight="bold" /> Close
                                    </Button>
                                )}
                                {e.status === "closed" && (
                                    <Button size="sm" onClick={() => setStatus(e.id, "active")} variant="outline" data-testid={`reopen-${e.id}`}>Reopen</Button>
                                )}

                                {/* Email blasts — inline to save vertical space */}
                                <div className="flex items-center gap-1.5 border-l border-slate-200 pl-2 ml-1" data-testid={`email-blast-${e.id}`}>
                                    <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Email</span>
                                    {(() => {
                                        const key = `${e.id}:welcome`;
                                        return (
                                            <button
                                                type="button"
                                                onClick={() => openEmailPreview(e, "welcome")}
                                                disabled={emailBusy === key}
                                                title="Welcome — notify eligible voters that an election has been scheduled in their precinct, with deadline"
                                                className="h-7 w-7 grid place-items-center rounded-md bg-civic-600 hover:bg-civic-700 disabled:opacity-60 text-white font-heading font-black text-xs tracking-tight shadow-sm transition-colors"
                                                data-testid={`email-welcome-${e.id}`}
                                            >
                                                {emailBusy === key ? "…" : "W"}
                                            </button>
                                        );
                                    })()}
                                    {(() => {
                                        const key = `${e.id}:reminder`;
                                        return (
                                            <button
                                                type="button"
                                                onClick={() => openEmailPreview(e, "reminder")}
                                                disabled={emailBusy === key}
                                                title="Reminder — send a vote reminder anytime before polls close"
                                                className="h-7 w-7 grid place-items-center rounded-md bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white font-heading font-black text-xs tracking-tight shadow-sm transition-colors"
                                                data-testid={`email-reminder-${e.id}`}
                                            >
                                                {emailBusy === key ? "…" : "R"}
                                            </button>
                                        );
                                    })()}
                                    {(() => {
                                        const key = `${e.id}:results`;
                                        const isOver = !!(e.ends_at && new Date(e.ends_at) <= new Date());
                                        return (
                                            <button
                                                type="button"
                                                onClick={() => openEmailPreview(e, "results")}
                                                disabled={!isOver || emailBusy === key}
                                                title={isOver ? "Results — notify all eligible voters that final results are now available" : "Results email available only after polls close"}
                                                className="h-7 w-7 grid place-items-center rounded-md bg-purple-600 hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-heading font-black text-xs tracking-tight shadow-sm transition-colors"
                                                data-testid={`email-results-${e.id}`}
                                            >
                                                {emailBusy === key ? "…" : "R"}
                                            </button>
                                        );
                                    })()}
                                    <button
                                        type="button"
                                        onClick={() => setCustomEmail({ election: e, subject: "", message: "" })}
                                        title="Custom message — compose a free-form email and send to every eligible voter"
                                        className="h-7 w-7 grid place-items-center rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-heading font-black text-xs tracking-tight shadow-sm transition-colors"
                                        data-testid={`email-custom-${e.id}`}
                                    >
                                        M
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setEmailHistoryElection(e)}
                                        title="Email history — every message sent for this election"
                                        className="h-7 px-2 grid place-items-center rounded-md bg-white border border-slate-300 hover:bg-slate-50 hover:border-civic-400 text-slate-700 hover:text-civic-700 text-[10px] font-heading font-bold tracking-tight transition-colors inline-flex gap-1"
                                        data-testid={`email-history-${e.id}`}
                                    >
                                        <ClockCounterClockwise size={12} weight="bold" />
                                        History
                                    </button>
                                </div>

                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setPreviewElection(e)}
                                    className="border-slate-300"
                                    data-testid={`view-ballot-${e.id}`}
                                >
                                    <Eye size={14} className="mr-1.5" weight="bold" /> View ballot
                                </Button>

                                <label
                                    className={`inline-flex items-center gap-1.5 h-8 px-2 rounded-md border cursor-pointer text-[11px] hash-mono transition-colors ${
                                        e.results_hidden
                                            ? "bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100"
                                            : "bg-white border-slate-300 text-slate-700 hover:border-amber-300 hover:bg-amber-50"
                                    }`}
                                    title={e.results_hidden
                                        ? "Public visitors currently see \"Results hidden by the election authority\" instead of tallies. Uncheck to publish."
                                        : "Prevent tallies from being displayed on the public Results page. Admins can still see them."}
                                    data-testid={`hide-results-label-${e.id}`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={!!e.results_hidden}
                                        onChange={() => toggleResultsHidden(e)}
                                        className="h-3.5 w-3.5 accent-amber-600"
                                        data-testid={`hide-results-${e.id}`}
                                    />
                                    <EyeSlash size={12} weight={e.results_hidden ? "fill" : "regular"} />
                                    Hide results
                                </label>

                                <a href={`/results/${e.id}`} className="ml-auto text-civic-600 text-sm font-medium underline-offset-4 hover:underline self-center">View results →</a>
                            </div>
                            </>
                            )}
                        </Card>
                    ))}
                </div>
            )}

            <BallotWizard
                open={!!previewElection}
                election={previewElection}
                onExit={() => setPreviewElection(null)}
                previewOnly
            />

            <ElectionEmailHistoryDialog
                election={emailHistoryElection}
                onClose={() => setEmailHistoryElection(null)}
            />

            <Dialog open={!!deleting} onOpenChange={(o) => { if (!o) { setDeleting(null); setDeleteChallenge(""); } }}>
                <DialogContent data-testid="delete-election-dialog">
                    <DialogHeader>
                        <div className="flex items-center gap-2">
                            <Warning size={22} weight="fill" className="text-red-600" />
                            <DialogTitle className="font-heading text-2xl">Delete this election?</DialogTitle>
                        </div>
                        <DialogDescription>
                            This permanently removes the election <span className="font-semibold">"{deleting?.title}"</span>
                            {deleting?.votes_count > 0
                                ? <> and its <span className="font-semibold">{deleting.votes_count} vote{deleting.votes_count === 1 ? "" : "s"}</span> from the chain. The chain will be re-linked so integrity stays valid.</>
                                : <>. No votes were cast for this election.</>}
                        </DialogDescription>
                    </DialogHeader>
                    {deleting?.votes_count > 0 && (
                        <div className="rounded-md border border-red-200 bg-red-50 p-3 space-y-2" data-testid="delete-challenge-block">
                            <div className="text-xs text-red-900 leading-snug">
                                Because <span className="font-semibold">{deleting.votes_count} vote{deleting.votes_count === 1 ? " has" : "s have"}</span> already been cast, please type the election title below to confirm:
                            </div>
                            <div className="hash-mono text-[11px] text-slate-700 bg-white border border-red-100 rounded px-2 py-1 select-all" data-testid="delete-challenge-target">
                                {deleting.title}
                            </div>
                            <Input
                                value={deleteChallenge}
                                onChange={(e) => setDeleteChallenge(e.target.value)}
                                placeholder="Type the election title exactly to enable delete"
                                className="hash-mono text-sm border-red-200 focus:border-red-400 focus:ring-red-200"
                                data-testid="delete-challenge-input"
                                autoFocus
                            />
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setDeleting(null); setDeleteChallenge(""); }} data-testid="delete-cancel-btn">Cancel</Button>
                        <Button
                            onClick={confirmDelete}
                            disabled={deleting?.votes_count > 0 && deleteChallenge.trim() !== (deleting?.title || "").trim()}
                            className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                            data-testid="delete-confirm-btn"
                        >
                            <TrashIcon size={16} className="mr-2" weight="bold" /> Delete election
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <EmailPreviewDialog
                preview={emailPreview}
                busy={emailPreview && emailBusy === `${emailPreview.election?.id}:${emailPreview.kind}`}
                onClose={() => setEmailPreview(null)}
                onSend={confirmAndSendEmail}
                onNoteChange={(note) => setEmailPreview((cur) => (cur ? { ...cur, note } : cur))}
                onRefresh={refreshEmailPreview}
                onToggleResendAll={(on) => emailPreview && openEmailPreview(emailPreview.election, emailPreview.kind, emailPreview.note || "", on)}
            />

            <CustomEmailDialog
                data={customEmail}
                onChange={(patch) => setCustomEmail((cur) => (cur ? { ...cur, ...patch } : cur))}
                onClose={() => setCustomEmail(null)}
                onSent={() => { setCustomEmail(null); reload(); }}
            />
        </div>
    );
}

function WelcomeResendToggle({ skipped, already, reincluded, on, disabled, onChange }) {
    return (
        <div className="mt-1.5 flex flex-wrap items-center gap-2" data-testid="welcome-resend-section">
            {!on && reincluded > 0 && (
                <span className="inline-flex items-center text-[10px] font-bold uppercase tracking-wider bg-amber-400/90 text-amber-950 rounded px-2 py-0.5" data-testid="reincluded-email-changed">
                    Including {reincluded} with a changed email
                </span>
            )}
            {!on && skipped > 0 && (
                <span className="inline-flex items-center text-[10px] font-bold uppercase tracking-wider bg-white/15 border border-white/30 rounded px-2 py-0.5" data-testid="skipped-already-welcomed">
                    Skipping {skipped} voter{skipped === 1 ? "" : "s"} already welcomed
                </span>
            )}
            <label className="inline-flex items-center gap-1.5 text-[11px] text-white/90 cursor-pointer">
                <Checkbox
                    checked={on}
                    disabled={disabled}
                    onCheckedChange={(v) => onChange(!!v)}
                    className="border-white/70 data-[state=checked]:bg-white data-[state=checked]:text-civic-700"
                    data-testid="welcome-resend-all-checkbox"
                />
                Resend to everyone (including {already} who already got it)
            </label>
        </div>
    );
}

function EmailPreviewDialog({ preview, busy, onClose, onSend, onNoteChange, onRefresh, onToggleResendAll }) {
    const note = preview?.note ?? "";
    useEffect(() => {
        if (!preview) return undefined;
        const t = setTimeout(() => onRefresh(note), 500);
        return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [note]);
    if (!preview) return null;
    const { election, kind, subject, html, recipient_count, sample_recipient, base_url, skipped_already_voted } = preview;
    const kindLabel = { welcome: "Welcome", reminder: "Reminder", results: "Results" }[kind] || kind;
    const accent = { welcome: "bg-civic-600", reminder: "bg-orange-600", results: "bg-purple-600" }[kind] || "bg-slate-600";

    return (
        <Dialog open={!!preview} onOpenChange={(o) => !o && onClose()}>
            <DialogContent
                className="w-[calc(100vw-1rem)] sm:w-full sm:max-w-4xl max-h-[92vh] overflow-hidden p-0 gap-0 grid-cols-1 [&>*]:min-w-0"
                data-testid="email-preview-dialog"
            >
                <div className={`${accent} text-white px-5 py-3 flex items-start justify-between gap-3`}>
                    <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-widest text-white/80 font-bold">{kindLabel} email preview</div>
                        <DialogTitle className="font-heading font-bold text-base sm:text-lg mt-0.5 truncate">{election?.title}</DialogTitle>
                        <DialogDescription className="text-white/85 text-[11px] mt-0.5 truncate">
                            Will send to <strong className="text-white">{recipient_count}</strong>{" "}
                            voter{recipient_count === 1 ? "" : "s"} with email on file
                            {recipient_count > 0 && sample_recipient?.name ? <> · preview rendered for <strong className="text-white">{sample_recipient.name}</strong></> : null}
                        </DialogDescription>
                        {kind === "reminder" && skipped_already_voted > 0 && (
                            <div className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-white/15 border border-white/30 rounded px-2 py-0.5" data-testid="skipped-already-voted">
                                Skipping {skipped_already_voted} voter{skipped_already_voted === 1 ? "" : "s"} who already voted
                            </div>
                        )}
                        {kind === "welcome" && preview.already_welcomed > 0 && (
                            <WelcomeResendToggle
                                skipped={preview.skipped_already_welcomed}
                                already={preview.already_welcomed}
                                reincluded={preview.reincluded_email_changed}
                                on={!!preview.resend_all}
                                disabled={busy}
                                onChange={onToggleResendAll}
                            />
                        )}
                    </div>
                </div>

                <div className="px-5 py-2 bg-slate-50 border-b border-slate-200 text-xs flex flex-wrap items-center gap-x-4 gap-y-1" data-testid="email-preview-meta">
                    <div><span className="text-slate-500">Subject:</span> <span className="font-semibold text-slate-900">{subject}</span></div>
                    <div className="hash-mono text-slate-500 truncate">links → {base_url}</div>
                </div>

                <div className="px-5 py-3 border-b border-slate-200 bg-white" data-testid="email-note-section">
                    <Label htmlFor="email-note" className="text-xs font-semibold text-slate-700">Personal note <span className="font-normal text-slate-400">(optional — shown at the top of the email)</span></Label>
                    <Textarea
                        id="email-note"
                        value={note}
                        onChange={(e) => onNoteChange(e.target.value.slice(0, 4000))}
                        placeholder="e.g. Polls close Friday at 5pm — please take two minutes to vote!"
                        rows={3}
                        className="mt-1.5 text-sm resize-y"
                        disabled={busy}
                        data-testid="email-note-input"
                    />
                    <div className="mt-1 text-[10px] text-slate-400 hash-mono text-right" data-testid="email-note-count">{note.length}/4000</div>
                </div>

                <div className="bg-white overflow-y-auto" style={{ maxHeight: "40vh" }} data-testid="email-preview-body">
                    <iframe
                        title="Email preview"
                        srcDoc={html}
                        sandbox=""
                        className="w-full"
                        style={{ minHeight: "40vh", border: "0" }}
                    />
                </div>

                <DialogFooter className="px-5 py-3 bg-slate-50 border-t border-slate-200">
                    <Button variant="outline" onClick={onClose} disabled={busy} data-testid="email-preview-cancel">
                        Cancel
                    </Button>
                    <Button
                        onClick={onSend}
                        disabled={busy || recipient_count === 0}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="email-preview-send"
                    >
                        <PaperPlaneTilt size={16} className="mr-2" weight="fill" />
                        {busy
                            ? "Sending…"
                            : recipient_count === 0
                                ? (kind === "welcome" && preview.already_welcomed > 0 ? "Everyone already welcomed" : "No eligible recipients")
                                : `Send to ${recipient_count} voter${recipient_count === 1 ? "" : "s"}`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function CustomEmailDialog({ data, onChange, onClose, onSent }) {
    const [busy, setBusy] = useState(false);
    const [preview, setPreview] = useState(null); // { subject, html_body, recipient_count, sample_recipient, base_url }
    const [previewing, setPreviewing] = useState(false);

    const election = data?.election;
    const subject = data?.subject || "";
    const message = data?.message || "";
    const canSend = subject.trim().length > 0 && message.trim().length > 0;

    const loadPreview = async () => {
        if (!election || !canSend) return;
        setPreviewing(true);
        try {
            const { data: p } = await api.post(
                `/admin/elections/${election.id}/email/custom/preview`,
                { subject: subject.trim(), message: message.trim() }
            );
            setPreview(p);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not render preview");
        }
        setPreviewing(false);
    };

    const send = async () => {
        if (!election || !canSend) return;
        setBusy(true);
        try {
            const { data: res } = await api.post(
                `/admin/elections/${election.id}/email/custom`,
                { subject: subject.trim(), message: message.trim() }
            );
            const summary = res.total_eligible === 0
                ? "No eligible voters with email on file"
                : `${res.sent} sent${res.failed ? ` · ${res.failed} failed` : ""}${res.bounced ? ` (${res.bounced} bounced — see Voter Roll)` : ""} of ${res.total_eligible} eligible`;
            toast.success(`Custom message — ${summary}`);
            onSent();
            setPreview(null);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not send custom email");
        }
        setBusy(false);
    };

    return (
        <Dialog
            open={!!data}
            onOpenChange={(o) => { if (!o) { setPreview(null); onClose(); } }}
        >
            <DialogContent
                className="w-[calc(100vw-1rem)] sm:w-full sm:max-w-3xl max-h-[92vh] overflow-hidden p-0 gap-0"
                data-testid="custom-email-dialog"
            >
                <div className="bg-emerald-600 text-white px-5 py-3">
                    <div className="text-[10px] uppercase tracking-widest text-white/80 font-bold">Custom message</div>
                    <DialogTitle className="font-heading font-bold text-base sm:text-lg mt-0.5 truncate">{election?.title}</DialogTitle>
                    <DialogDescription className="text-white/85 text-[11px] mt-0.5">
                        Sends to every eligible voter with email on file. Uses the Welcome visual format.
                    </DialogDescription>
                </div>

                <div className="px-5 py-4 overflow-y-auto space-y-3 bg-white" style={{ maxHeight: "60vh" }}>
                    {!preview && (
                        <>
                            <div>
                                <Label htmlFor="custom-email-subject" className="text-xs font-bold uppercase tracking-wider text-slate-700">Subject</Label>
                                <Input
                                    id="custom-email-subject"
                                    value={subject}
                                    onChange={(ev) => onChange({ subject: ev.target.value })}
                                    placeholder={`A message about ${election?.title || "this election"}`}
                                    className="mt-1"
                                    data-testid="custom-email-subject"
                                />
                            </div>
                            <div>
                                <Label htmlFor="custom-email-message" className="text-xs font-bold uppercase tracking-wider text-slate-700">Message</Label>
                                <textarea
                                    id="custom-email-message"
                                    value={message}
                                    onChange={(ev) => onChange({ message: ev.target.value })}
                                    rows={10}
                                    placeholder="Write your message to every eligible voter. Blank lines become paragraphs."
                                    className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm leading-6 shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                    data-testid="custom-email-message"
                                />
                                <p className="text-[11px] text-slate-500 mt-1">Plain text. HTML in your message is escaped — line breaks and blank lines are preserved.</p>
                            </div>
                        </>
                    )}

                    {preview && (
                        <>
                            <div className="text-xs flex flex-wrap items-center gap-x-4 gap-y-1 bg-slate-50 border border-slate-200 rounded-md px-3 py-2" data-testid="custom-email-preview-meta">
                                <div><span className="text-slate-500">Subject:</span> <span className="font-semibold text-slate-900">{preview.subject}</span></div>
                                <div><span className="text-slate-500">Will send to:</span> <strong className="text-slate-900">{preview.recipient_count}</strong> voter{preview.recipient_count === 1 ? "" : "s"}</div>
                                <div className="hash-mono text-slate-500 truncate">links → {preview.base_url}</div>
                            </div>
                            <iframe
                                title="Custom email preview"
                                srcDoc={preview.html_body}
                                sandbox=""
                                className="w-full rounded-md border border-slate-200"
                                style={{ minHeight: "50vh", border: "0" }}
                                data-testid="custom-email-preview-body"
                            />
                        </>
                    )}
                </div>

                <DialogFooter className="px-5 py-3 bg-slate-50 border-t border-slate-200">
                    <Button variant="outline" onClick={() => { setPreview(null); onClose(); }} disabled={busy} data-testid="custom-email-cancel">
                        Cancel
                    </Button>
                    {!preview && (
                        <Button
                            onClick={loadPreview}
                            disabled={!canSend || previewing}
                            variant="outline"
                            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                            data-testid="custom-email-preview-btn"
                        >
                            {previewing ? "Rendering…" : "Preview"}
                        </Button>
                    )}
                    {preview && (
                        <Button
                            variant="outline"
                            onClick={() => setPreview(null)}
                            disabled={busy}
                            data-testid="custom-email-edit"
                        >
                            Edit
                        </Button>
                    )}
                    <Button
                        onClick={send}
                        disabled={!canSend || busy || (preview && preview.recipient_count === 0)}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="custom-email-send"
                    >
                        <PaperPlaneTilt size={16} className="mr-2" weight="fill" />
                        {busy ? "Sending…" : preview
                            ? (preview.recipient_count === 0 ? "No eligible recipients" : `Send to ${preview.recipient_count} voter${preview.recipient_count === 1 ? "" : "s"}`)
                            : "Send to all eligible voters"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}



function StatusBadge({ status, resultsHref, testId }) {
    const map = {
        draft: { c: "bg-slate-100 text-slate-700 border-slate-300", t: "DRAFT" },
        active: { c: "bg-emerald-100 text-emerald-800 border-emerald-300", t: "ACTIVE" },
        closed: { c: "bg-amber-100 text-amber-900 border-amber-300", t: "CLOSED" },
    };
    const s = map[status] || map.draft;
    // Clicking the CLOSED badge should whisk the admin over to the public
    // Results page for that election — feels far more discoverable than an
    // "eye" icon buried in the actions column.
    if (status === "closed" && resultsHref) {
        return (
            <Link
                to={resultsHref}
                className={`overline px-2 py-0.5 rounded-sm border ${s.c} inline-flex items-center gap-1 hover:bg-amber-200 hover:border-amber-400 hover:text-amber-950 transition-colors cursor-pointer`}
                title="View certified results"
                data-testid={testId}
            >
                {s.t}
                <ArrowRight size={9} weight="bold" />
            </Link>
        );
    }
    return <span className={`overline px-2 py-0.5 rounded-sm border ${s.c}`} data-testid={testId}>{s.t}</span>;
}

function VotersPanel({ voters, reload }) {
    const nav = useNavigate();
    const { refresh } = useAuth();
    const [emailHistoryVoter, setEmailHistoryVoter] = useState(null);

    const impersonateVoter = async (voter) => {
        try {
            const adminToken = localStorage.getItem("cc_token");
            if (adminToken) localStorage.setItem("cc_admin_token", adminToken);
            const { data } = await api.post(`/admin/voters/${voter.voter_id}/impersonate`);
            if (data?.token) {
                localStorage.setItem("cc_token", data.token);
                await refresh();
                toast.success(`Signed in as ${voter.name} (${voter.voter_id})`);
                nav("/vote");
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not impersonate this voter");
        }
    };
    const [open, setOpen] = useState(false);
    const [vid, setVid] = useState("");
    const [pin, setPin] = useState("");
    const [name, setName] = useState("");
    const [address, setAddress] = useState("");
    const [email, setEmail] = useState("");
    const [mobile, setMobile] = useState("");
    const [precinct, setPrecinct] = useState("9912");
    const [dob, setDob] = useState("");
    const [importResult, setImportResult] = useState(null);
    const [importing, setImporting] = useState(false);
    const fileRef = useRef(null);

    // Edit / delete state
    const [editing, setEditing] = useState(null);
    const [resendFor, setResendFor] = useState(null);    const [editPin, setEditPin] = useState("");
    const [editName, setEditName] = useState("");
    const [editAddress, setEditAddress] = useState("");
    const [editEmail, setEditEmail] = useState("");
    const [editMobile, setEditMobile] = useState("");
    const [editPrecinct, setEditPrecinct] = useState("");
    const [editDob, setEditDob] = useState("");
    const [deleting, setDeleting] = useState(null);

    // Address validation (Census geocoder, non-blocking, debounced)
    const [addrCheck, setAddrCheck] = useState(null); // null | { loading: true } | server response
    useEffect(() => {
        const a = (address || "").trim();
        if (!open || a.length < 5) { setAddrCheck(null); return; }
        setAddrCheck({ loading: true });
        const h = setTimeout(async () => {
            try {
                const { data } = await api.get("/admin/voters/validate-address", { params: { address: a } });
                setAddrCheck(data);
            } catch {
                setAddrCheck(null);
            }
        }, 700);
        return () => clearTimeout(h);
    }, [address, open]);

    const create = async () => {
        if (!vid.trim() || !name.trim() || !address.trim()) {
            toast.error("Voter ID, name, and address required");
            return;
        }
        if (/\b(p\.?\s*o\.?|post\s*office)\s*box\b/i.test(address)) {
            toast.error("P.O. Boxes are not accepted — please provide a physical street address");
            return;
        }
        try {
            await api.post("/admin/voters", {
                voter_id: vid.trim(),
                pin: pin || null,
                name: name.trim(),
                address: address.trim(),
                email: email.trim() || null,
                mobile: mobile.trim() || null,
                dob: dob || null,
                precinct: precinct.trim() || "9912",
            });
            toast.success(`Voter ${vid.toUpperCase()} registered · initial PIN = ${pin ? "(custom)" : vid.toUpperCase()}`);
            setOpen(false);
            setVid(""); setPin(""); setName(""); setAddress(""); setEmail(""); setMobile(""); setDob("");
            setPrecinct("9912");
            reload();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    const openEdit = (v) => {
        setEditing(v);
        setEditPin("");
        setEditName(v.name || "");
        setEditAddress(v.address || "");
        setEditEmail(v.email || "");
        setEditMobile(v.mobile || "");
        setEditDob(v.dob || "");
        setEditPrecinct(v.precinct || "9912");
    };

    const saveEdit = async () => {
        if (!editing) return;
        try {
            const { data: saved } = await api.put(`/admin/voters/${editing.id}`, {
                name: editName.trim() || null,
                address: editAddress.trim() || null,
                email: editEmail.trim(),
                mobile: editMobile.trim(),
                dob: editDob,
                precinct: editPrecinct.trim() || null,
                pin: editPin.trim() || null,
            });
            toast.success(`Voter ${editing.voter_id} updated${editPin ? " · PIN changed" : ""}`);
            setEditing(null);
            if (saved?.email_changed) setResendFor({ voter: saved, elections: saved.resend_elections });
            reload();
        } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    };

    const confirmDelete = async () => {
        if (!deleting) return;
        try {
            const { data } = await api.delete(`/admin/voters/${deleting.id}`);
            toast.success(`Voter ${data.voter_id} deleted · ${data.vote_log_removed} log entries · ${data.blocks_removed} chain blocks removed`);
            setDeleting(null);
            reload();
        } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
    };

    const downloadFile = async (path, filename) => {
        try {
            const res = await api.get(path, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url; a.download = filename; document.body.appendChild(a); a.click();
            a.remove(); window.URL.revokeObjectURL(url);
        } catch { toast.error("Download failed"); }
    };

    const onUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        setImporting(true);
        try {
            const { data } = await api.post("/admin/voters/import", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setImportResult(data);
            toast.success(`Imported ${data.created_count} voters · ${data.skipped_count} skipped`);
            reload();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Import failed");
        }
        setImporting(false);
        if (fileRef.current) fileRef.current.value = "";
    };

    // Row classification: red for voters > 100 years old (data hygiene flag),
    // green for "active" voters who have cast a ballot within the last 2 years.
    // Red takes precedence over green because age outliers are a higher-priority
    // signal for admins than recent activity.
    const NOW = Date.now();
    const TWO_YEARS_MS = 2 * 365.25 * 24 * 60 * 60 * 1000;
    const computeAge = (dobStr) => {
        if (!dobStr) return null;
        const d = new Date(dobStr);
        if (Number.isNaN(d.getTime())) return null;
        const today = new Date();
        let age = today.getFullYear() - d.getFullYear();
        const m = today.getMonth() - d.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
        return age;
    };
    const classifyVoter = (v) => {
        const age = computeAge(v.dob);
        if (age !== null && age > 100) return "over100";
        if (v.last_voted_at) {
            const ts = new Date(v.last_voted_at).getTime();
            if (!Number.isNaN(ts) && NOW - ts <= TWO_YEARS_MS) return "active";
        }
        return "default";
    };

    // ── Sort state (client-side, sticks while the panel is mounted) ──
    const [sortBy, setSortBy] = useState({ key: "created_at", dir: "desc" });
    const toggleSort = (key) => {
        setSortBy((prev) => {
            if (prev.key !== key) return { key, dir: "asc" };
            return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
        });
    };
    const [bouncedOnly, setBouncedOnly] = useState(false);
    const bouncedCount = useMemo(() => voters.filter((v) => v.email_bounced).length, [voters]);
    const sortedVoters = useMemo(() => {
        const arr = bouncedOnly ? voters.filter((v) => v.email_bounced) : [...voters];
        const { key, dir } = sortBy;
        const mult = dir === "asc" ? 1 : -1;
        arr.sort((a, b) => {
            const av = (a?.[key] ?? "").toString().toLowerCase();
            const bv = (b?.[key] ?? "").toString().toLowerCase();
            if (av < bv) return -1 * mult;
            if (av > bv) return 1 * mult;
            return 0;
        });
        return arr;
    }, [voters, sortBy, bouncedOnly]);

    const SortHeader = ({ k, label, className = "" }) => {
        const active = sortBy.key === k;
        const Arrow = active && sortBy.dir === "desc" ? CaretDown : CaretUp;
        return (
            <TableHead className={`overline text-slate-500 ${className}`}>
                <button
                    type="button"
                    onClick={() => toggleSort(k)}
                    className={`inline-flex items-center gap-1 hover:text-slate-900 transition-colors ${active ? "text-civic-700" : ""}`}
                    data-testid={`voter-sort-${k}`}
                    aria-sort={active ? (sortBy.dir === "asc" ? "ascending" : "descending") : "none"}
                >
                    <span>{label}</span>
                    <Arrow
                        size={11}
                        weight="bold"
                        className={active ? "opacity-100" : "opacity-30"}
                    />
                </button>
            </TableHead>
        );
    };

    return (
        <div>
            {resendFor && (
                <ResendWelcomeDialog
                    voter={resendFor.voter}
                    initialElections={resendFor.elections}
                    onClose={() => setResendFor(null)}
                    onDone={reload}
                />
            )}

            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div>
                    <h2 className="font-heading font-bold text-xl text-slate-900">Voter roll</h2>
                    <p className="text-sm text-slate-500">Anonymous to the chain — only these IDs can sign in. Initial PIN defaults to the Voter ID.</p>
                    {(bouncedCount > 0 || bouncedOnly) && (
                        <button
                            type="button"
                            onClick={() => setBouncedOnly((b) => !b)}
                            className={`mt-2 inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1 border transition-colors ${bouncedOnly ? "bg-red-600 text-white border-red-600" : "bg-red-50 text-red-700 border-red-200 hover:bg-red-100"}`}
                            data-testid="bounced-filter-btn"
                        >
                            <WarningCircle size={13} weight="fill" /> {bouncedOnly ? "Showing bounced only · show all" : `${bouncedCount} bounced email${bouncedCount === 1 ? "" : "s"} · show only these`}
                        </button>
                    )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <input
                        ref={fileRef}
                        type="file"
                        accept=".xlsx,.xlsm"
                        onChange={onUpload}
                        className="hidden"
                        data-testid="voter-import-input"
                    />
                    <Button
                        variant="outline"
                        className="border-slate-300"
                        onClick={() => downloadFile("/admin/voters/template", "voter_roll_template.xlsx")}
                        data-testid="download-template-btn"
                    >
                        <FileXls size={16} className="mr-1.5" /> Template
                    </Button>
                    <Button
                        variant="outline"
                        className="border-slate-300"
                        onClick={() => fileRef.current?.click()}
                        disabled={importing}
                        data-testid="import-voters-btn"
                    >
                        <UploadSimple size={16} className="mr-1.5" /> {importing ? "Importing…" : "Import .xlsx"}
                    </Button>
                    <Button
                        variant="outline"
                        className="border-slate-300"
                        onClick={() => downloadFile("/admin/voters/export", "voter_roll.xlsx")}
                        data-testid="export-voters-btn"
                    >
                        <DownloadSimple size={16} className="mr-1.5" /> Export
                    </Button>
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button className="bg-civic-600 hover:bg-civic-700 text-white" data-testid="open-create-voter-btn">
                                <Plus size={16} className="mr-2" weight="bold" /> Register voter
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-lg" data-testid="create-voter-dialog">
                            <DialogHeader>
                                <DialogTitle className="font-heading text-2xl">Register voter</DialogTitle>
                                <DialogDescription>Issue credentials. Physical address required (no P.O. Boxes). PIN defaults to the Voter ID for the initial login.</DialogDescription>
                            </DialogHeader>
                            <div className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label className="overline text-slate-500">Voter ID</Label>
                                        <Input value={vid} onChange={(e) => setVid(e.target.value)} placeholder="VOTER001" className="font-mono uppercase" data-testid="new-voter-id" />
                                    </div>
                                    <div>
                                        <Label className="overline text-slate-500">PIN <span className="text-slate-400 normal-case">(optional)</span></Label>
                                        <Input type="password" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="defaults to Voter ID" className="font-mono" data-testid="new-voter-pin" />
                                    </div>
                                </div>
                                <div>
                                    <Label className="overline text-slate-500">Full name</Label>
                                    <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" data-testid="new-voter-name" />
                                </div>
                                <div>
                                    <Label className="overline text-slate-500">Physical address (no P.O. Box)</Label>
                                    <Textarea
                                        rows={2}
                                        value={address}
                                        onChange={(e) => setAddress(e.target.value)}
                                        placeholder="123 Main St, Denton, TX 76201"
                                        data-testid="new-voter-address"
                                    />
                                    <AddressValidationHint check={addrCheck} onUseMatch={(m) => setAddress(m)} />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label className="overline text-slate-500">Email <span className="text-slate-400 normal-case">(optional)</span></Label>
                                        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jane@example.com" data-testid="new-voter-email" />
                                    </div>
                                    <div>
                                        <Label className="overline text-slate-500">Mobile <span className="text-slate-400 normal-case">(optional)</span></Label>
                                        <Input type="tel" value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="+1 555 555 1212" className="font-mono" data-testid="new-voter-mobile" />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label className="overline text-slate-500">Date of birth <span className="text-slate-400 normal-case">(optional)</span></Label>
                                        <Input type="date" value={dob} onChange={(e) => setDob(e.target.value)} className="font-mono" data-testid="new-voter-dob" />
                                    </div>
                                    <div>
                                        <Label className="overline text-slate-500">Precinct <span className="text-slate-400 normal-case">(comma-separate)</span></Label>
                                        <Input value={precinct} onChange={(e) => setPrecinct(e.target.value)} placeholder="9912 or SCHO,9912" className="font-mono" data-testid="new-voter-precinct" />
                                    </div>
                                </div>
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                                <Button onClick={create} className="bg-civic-600 hover:bg-civic-700 text-white" data-testid="create-voter-submit">Register</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            {importResult && (
                <Card className="p-4 mb-4 bg-civic-50 border-civic-200" data-testid="import-result">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <div className="font-semibold text-slate-900">
                                Imported <span className="text-emerald-700">{importResult.created_count}</span> ·
                                Skipped <span className="text-red-700">{importResult.skipped_count}</span>
                            </div>
                            {importResult.skipped?.length > 0 && (
                                <ul className="mt-2 text-xs text-slate-600 space-y-0.5 max-h-28 overflow-auto">
                                    {importResult.skipped.slice(0, 20).map((s, i) => (
                                        <li key={i} className="hash-mono">
                                            row {s.row}{s.voter_id ? ` (${s.voter_id})` : ""} — {s.reason}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <Button size="sm" variant="ghost" onClick={() => setImportResult(null)} data-testid="dismiss-import-result"><X size={14} /></Button>
                    </div>
                </Card>
            )}

            <Card className="border-slate-200 bg-white overflow-hidden" data-testid="voter-table-card">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-2.5 border-b border-slate-200 bg-slate-50/60 text-xs text-slate-600" data-testid="voter-legend">
                    <span className="overline text-slate-500">Legend</span>
                    <span className="inline-flex items-center gap-1.5">
                        <span className="inline-block h-3 w-3 rounded-sm bg-emerald-100 border border-emerald-300" aria-hidden="true" />
                        Active voter <span className="text-slate-400">(voted in last 2 years)</span>
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                        <span className="inline-block h-3 w-3 rounded-sm bg-red-100 border border-red-300" aria-hidden="true" />
                        Over 100 <span className="text-slate-400">(verify date of birth)</span>
                    </span>
                </div>
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-slate-50 hover:bg-slate-50">
                                <SortHeader k="voter_id" label="Voter ID" />
                                <SortHeader k="name" label="Name" />
                                <SortHeader k="email" label="Email" />
                                <SortHeader k="address" label="Address" />
                                <SortHeader k="precinct" label="Precinct" />
                                <SortHeader k="created_at" label="Registered" />
                                <TableHead className="overline text-slate-500 text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {sortedVoters.length === 0 ? (
                                <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-10 hash-mono text-xs">no voters registered</TableCell></TableRow>
                            ) : sortedVoters.map((v) => {
                                const status = classifyVoter(v);
                                const rowClass = status === "over100"
                                    ? "bg-red-50 hover:bg-red-100/70"
                                    : status === "active"
                                        ? "bg-emerald-50 hover:bg-emerald-100/70"
                                        : "";
                                return (
                                <TableRow key={v.id} data-testid={`voter-row-${v.voter_id}`} data-voter-status={status} className={rowClass}>
                                    <TableCell className="hash-mono font-semibold">
                                        <button
                                            type="button"
                                            onClick={() => impersonateVoter(v)}
                                            className="text-civic-700 hover:text-civic-900 hover:underline underline-offset-4 cursor-pointer"
                                            title={`Sign in as ${v.name} for testing`}
                                            data-testid={`impersonate-voter-${v.voter_id}`}
                                        >
                                            {v.voter_id}
                                        </button>
                                    </TableCell>
                                    <TableCell className="text-slate-900 whitespace-nowrap">
                                        <button
                                            type="button"
                                            onClick={() => setEmailHistoryVoter(v)}
                                            className="text-slate-900 hover:text-civic-700 hover:underline underline-offset-4"
                                            title={`View communications with ${v.name}`}
                                            data-testid={`voter-emails-${v.voter_id}`}
                                        >
                                            {v.name}
                                        </button>
                                    </TableCell>
                                    <TableCell className="text-slate-700 text-sm whitespace-nowrap">
                                        {v.email || <span className="text-slate-400 italic">—</span>}
                                        {v.email_bounced && (
                                            <div
                                                className="mt-1 flex w-fit items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-red-700 bg-red-50 border border-red-200 rounded px-1.5 py-0.5"
                                                title={`${v.email_bounce_reason || "Rejected by mail server"}${v.email_bounced_at ? ` · ${new Date(v.email_bounced_at).toLocaleString()}` : ""}`}
                                                data-testid={`email-bounced-flag-${v.voter_id}`}
                                            >
                                                <WarningCircle size={11} weight="fill" /> Email bounced · fix address
                                            </div>
                                        )}
                                        {v.email_resend_pending && (
                                            <button
                                                type="button"
                                                onClick={() => setResendFor({ voter: v, elections: null })}
                                                className="mt-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 hover:bg-amber-100"
                                                title={`Earlier emails went to ${v.previous_email || "the old address"}`}
                                                data-testid={`email-changed-flag-${v.voter_id}`}
                                            >
                                                <Warning size={11} weight="fill" /> Email changed · resend
                                            </button>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-slate-700 text-sm max-w-xs">
                                        <div className="flex items-start gap-1.5">
                                            <MapPin size={14} className="text-slate-400 mt-0.5 shrink-0" />
                                            <span>{v.address || <span className="text-slate-400 italic">—</span>}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell><Badge variant="outline" className="border-civic-200 text-civic-700 hash-mono">{v.precinct || "—"}</Badge></TableCell>
                                    <TableCell className="hash-mono text-xs text-slate-500 whitespace-nowrap">{v.created_at?.slice(0, 19).replace("T", " ")}</TableCell>
                                    <TableCell className="text-right whitespace-nowrap">
                                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(v)} data-testid={`edit-voter-${v.voter_id}`}>
                                            <PencilSimple size={16} className="text-slate-600" />
                                        </Button>
                                        <Button size="icon" variant="ghost" className="h-8 w-8 hover:bg-red-50" onClick={() => setDeleting(v)} data-testid={`delete-voter-${v.voter_id}`}>
                                            <TrashIcon size={16} className="text-red-600" />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </div>
            </Card>

            {/* Edit dialog */}
            <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
                <DialogContent className="sm:max-w-lg" data-testid="edit-voter-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl">Edit voter</DialogTitle>
                        <DialogDescription>
                            Updating <span className="hash-mono text-civic-700">{editing?.voter_id}</span>. Voter ID is immutable. Leave PIN blank to keep the current one.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label className="overline text-slate-500">Full name</Label>
                            <Input value={editName} onChange={(e) => setEditName(e.target.value)} data-testid="edit-voter-name" />
                        </div>
                        <div>
                            <Label className="overline text-slate-500">Email <span className="text-slate-400 normal-case">(blank = remove)</span></Label>
                            <Input type="email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} placeholder="jane@example.com" data-testid="edit-voter-email" />
                        </div>
                        <div>
                            <Label className="overline text-slate-500">Mobile phone <span className="text-slate-400 normal-case">(blank = remove)</span></Label>
                            <Input type="tel" value={editMobile} onChange={(e) => setEditMobile(e.target.value)} placeholder="+1 555 555 1212" className="font-mono" data-testid="edit-voter-mobile" />
                        </div>
                        <div>
                            <Label className="overline text-slate-500">Date of birth <span className="text-slate-400 normal-case">(blank = remove)</span></Label>
                            <Input type="date" value={editDob} onChange={(e) => setEditDob(e.target.value)} className="font-mono" data-testid="edit-voter-dob" />
                        </div>
                        <div>
                            <Label className="overline text-slate-500">Physical address (no P.O. Box)</Label>
                            <Textarea rows={2} value={editAddress} onChange={(e) => setEditAddress(e.target.value)} data-testid="edit-voter-address" />
                        </div>
                        <div>
                            <Label className="overline text-slate-500">Precinct <span className="text-slate-400 normal-case">(comma-separate for multi-district voters, e.g. SCHO,9912)</span></Label>
                            <Input value={editPrecinct} onChange={(e) => setEditPrecinct(e.target.value)} className="font-mono" data-testid="edit-voter-precinct" />
                        </div>
                        <div>
                            <Label className="overline text-slate-500">New PIN <span className="text-slate-400 normal-case">(leave blank to keep current)</span></Label>
                            <Input type="password" value={editPin} onChange={(e) => setEditPin(e.target.value)} placeholder="••••" className="font-mono" data-testid="edit-voter-pin" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                        <Button onClick={saveEdit} className="bg-civic-600 hover:bg-civic-700 text-white" data-testid="edit-voter-submit">Save changes</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete dialog */}
            <Dialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
                <DialogContent data-testid="delete-voter-dialog">
                    <DialogHeader>
                        <div className="flex items-center gap-2">
                            <Warning size={22} weight="fill" className="text-red-600" />
                            <DialogTitle className="font-heading text-2xl">Delete voter?</DialogTitle>
                        </div>
                        <DialogDescription>
                            Removes <span className="hash-mono text-civic-700">{deleting?.voter_id}</span> ({deleting?.name}) from the roll. If they voted in any election, one anonymous block per election will also be removed and the chain re-linked so audit totals stay reconciled.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleting(null)} data-testid="delete-voter-cancel">Cancel</Button>
                        <Button onClick={confirmDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="delete-voter-confirm">
                            <TrashIcon size={16} className="mr-2" weight="bold" /> Delete voter
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <VoterEmailHistoryDialog
                voter={emailHistoryVoter}
                onClose={() => setEmailHistoryVoter(null)}
            />
        </div>
    );
}


function DangerZone({ reload }) {
    const [busy, setBusy] = useState(null); // 'votes' | 'voters' | 'emails' | null
    const [confirm, setConfirm] = useState(null); // { kind, title, body, action }
    const [confirmText, setConfirmText] = useState("");

    // Wipe-votes selector state — separate from the generic RESET confirm below
    // because the user picks *which* elections' votes to erase (or "all") from a
    // checklist first, then confirms with the RESET typedown.
    const [wipeSelectOpen, setWipeSelectOpen] = useState(false);
    const [wipeElections, setWipeElections] = useState([]);      // fetched from /admin/elections
    const [wipeElectionsLoading, setWipeElectionsLoading] = useState(false);
    const [wipeSelected, setWipeSelected] = useState(new Set()); // election ids ticked
    const [wipeAll, setWipeAll] = useState(false);               // "wipe every election" toggle
    const openWipeSelector = async () => {
        setWipeSelected(new Set());
        setWipeAll(false);
        setWipeSelectOpen(true);
        setWipeElectionsLoading(true);
        try {
            const { data } = await api.get("/admin/elections");
            setWipeElections(data || []);
        } catch {
            setWipeElections([]);
        }
        setWipeElectionsLoading(false);
    };
    const toggleWipeElection = (id) => {
        setWipeSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const proceedWipeVotes = () => {
        const ids = wipeAll ? [] : Array.from(wipeSelected);
        if (!wipeAll && ids.length === 0) return;
        setWipeSelectOpen(false);
        setConfirmText("");
        setConfirm({
            kind: "votes",
            title: wipeAll ? "Wipe votes for EVERY election?" : `Wipe votes for ${ids.length} election${ids.length === 1 ? "" : "s"}?`,
            body: wipeAll
                ? "Deletes every vote block on the chain (genesis kept) and clears the participation log across ALL elections. Voter accounts and elections remain. Type RESET to confirm."
                : `Deletes vote blocks + participation-log rows only for the selected election${ids.length === 1 ? "" : "s"}. Blocks for other elections stay on the chain (but their indices may shift as earlier blocks are removed — receipt hashes are re-computed). Type RESET to confirm.`,
            endpoint: "/admin/votes/all",
            payload: wipeAll ? {} : { election_ids: ids },
            done: wipeAll ? "All votes cleared" : `Votes cleared for ${ids.length} election${ids.length === 1 ? "" : "s"}`,
        });
    };

    const ask = (kind) => {
        setConfirmText("");
        if (kind === "voters") {
            setConfirm({
                kind: "voters",
                title: "Wipe all voters AND votes?",
                body: "Deletes every registered voter, every vote block (genesis kept), and the participation log. Elections remain. Type RESET to confirm.",
                endpoint: "/admin/voters/all",
                done: "Voters and votes cleared",
            });
        } else if (kind === "emails") {
            setConfirm({
                kind: "emails",
                title: "Clear all email history?",
                body: "Removes every entry from the communications log so the per-voter \"Communications\" modal starts empty. Voters, elections, votes, and chain are untouched. Useful after restoring a backup or moving between environments. Type RESET to confirm.",
                endpoint: "/admin/email-log/all",
                done: "Email history cleared",
            });
        }
    };

    const execute = async () => {
        if (!confirm) return;
        setBusy(confirm.kind);
        // Destructive endpoints require an explicit `?confirm=YES_WIPE_ALL`
        // guardrail on the backend. Only add it for the two nuclear resets —
        // /admin/voters/all and /admin/votes/all. Email-log clearing is safe.
        const guarded = confirm.endpoint === "/admin/voters/all" || confirm.endpoint === "/admin/votes/all";
        const url = guarded
            ? `${confirm.endpoint}?confirm=YES_WIPE_ALL`
            : confirm.endpoint;
        try {
            const { data } = confirm.payload
                ? await api.delete(url, { data: confirm.payload })
                : await api.delete(url);
            if (confirm.kind === "emails") {
                toast.success(`${confirm.done} · ${data.email_log_deleted ?? 0} entries removed`);
            } else {
                toast.success(`${confirm.done} · ${data.voters_deleted ?? 0} voters · ${data.blocks_deleted ?? 0} blocks · ${data.vote_log_deleted ?? 0} log entries`);
            }
            setConfirm(null);
            reload();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Reset failed");
        }
        setBusy(null);
    };

    return (
        <div data-testid="danger-zone">
            <div className="mb-5">
                <h2 className="font-heading font-bold text-xl text-slate-900">Test mode · Danger Zone</h2>
                <p className="text-sm text-slate-500">Destructive resets for testing. Genesis block is preserved; elections (drafts/active) are not touched.</p>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
                <Card className="p-6 border-amber-200 bg-amber-50" data-testid="wipe-votes-card">
                    <div className="flex items-start gap-3">
                        <div className="h-10 w-10 grid place-items-center rounded-md bg-white border border-amber-200 shrink-0">
                            <Cube size={20} weight="duotone" className="text-amber-600" />
                        </div>
                        <div className="flex-1">
                            <div className="overline text-amber-700">Reset votes</div>
                            <h3 className="font-heading font-bold text-lg text-slate-900 mt-1">Wipe votes</h3>
                            <p className="text-sm text-slate-700 mt-1">Pick one or more elections to reset — or wipe every election in one shot. Voters and elections stay intact so you can re-run a vote.</p>
                            <Button
                                onClick={openWipeSelector}
                                disabled={busy === "votes"}
                                className="mt-4 bg-amber-600 hover:bg-amber-700 text-white"
                                data-testid="wipe-votes-btn"
                            >
                                <Trash size={16} className="mr-2" weight="bold" /> Choose &amp; wipe
                            </Button>
                        </div>
                    </div>
                </Card>

                <Card className="p-6 border-red-200 bg-red-50" data-testid="wipe-voters-card">
                    <div className="flex items-start gap-3">
                        <div className="h-10 w-10 grid place-items-center rounded-md bg-white border border-red-200 shrink-0">
                            <Warning size={20} weight="duotone" className="text-red-600" />
                        </div>
                        <div className="flex-1">
                            <div className="overline text-red-700">Reset voters + votes</div>
                            <h3 className="font-heading font-bold text-lg text-slate-900 mt-1">Wipe voter roll &amp; chain</h3>
                            <p className="text-sm text-slate-700 mt-1">Clears every registered voter, the vote chain (down to genesis) and the participation log. Use to start a clean test cycle.</p>
                            <Button
                                onClick={() => ask("voters")}
                                disabled={busy === "voters"}
                                className="mt-4 bg-red-600 hover:bg-red-700 text-white"
                                data-testid="wipe-voters-btn"
                            >
                                <Trash size={16} className="mr-2" weight="bold" /> Wipe voters &amp; votes
                            </Button>
                        </div>
                    </div>
                </Card>

                <Card className="p-6 border-slate-200 bg-slate-50" data-testid="wipe-emails-card">
                    <div className="flex items-start gap-3">
                        <div className="h-10 w-10 grid place-items-center rounded-md bg-white border border-slate-200 shrink-0">
                            <EnvelopeSimple size={20} weight="duotone" className="text-slate-600" />
                        </div>
                        <div className="flex-1">
                            <div className="overline text-slate-600">Reset communications</div>
                            <h3 className="font-heading font-bold text-lg text-slate-900 mt-1">Clear email history</h3>
                            <p className="text-sm text-slate-700 mt-1">Drops every entry from the email log so the per-voter <em>Communications</em> modal starts empty. Voters, elections, votes, and the chain are untouched. Use after restoring a backup from another environment.</p>
                            <Button
                                onClick={() => ask("emails")}
                                disabled={busy === "emails"}
                                className="mt-4 bg-slate-700 hover:bg-slate-800 text-white"
                                data-testid="wipe-emails-btn"
                            >
                                <Trash size={16} className="mr-2" weight="bold" /> Clear email history
                            </Button>
                        </div>
                    </div>
                </Card>
            </div>

            <Dialog open={wipeSelectOpen} onOpenChange={(o) => !o && setWipeSelectOpen(false)}>
                <DialogContent className="w-[calc(100vw-1rem)] sm:w-full sm:max-w-lg p-0 gap-0 overflow-hidden" data-testid="wipe-votes-select-dialog">
                    <div className="bg-amber-600 text-white px-5 py-3">
                        <div className="text-[10px] uppercase tracking-widest text-amber-100 font-bold flex items-center gap-1.5">
                            <Trash size={11} weight="bold" /> Choose elections to wipe
                        </div>
                        <DialogTitle className="font-heading font-bold text-base sm:text-lg mt-0.5">Which elections should be reset?</DialogTitle>
                        <DialogDescription className="text-white/90 text-[11px] mt-0.5">
                            Only the vote blocks + participation-log rows for the selected elections are removed. Everything else on the chain is preserved and re-linked.
                        </DialogDescription>
                    </div>

                    <div className="px-5 py-3 border-b border-slate-200 bg-white">
                        <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="wipe-all-toggle">
                            <input
                                type="checkbox"
                                checked={wipeAll}
                                onChange={(e) => { setWipeAll(e.target.checked); if (e.target.checked) setWipeSelected(new Set()); }}
                                className="h-4 w-4 accent-amber-600"
                            />
                            <span className="font-heading font-bold text-sm text-slate-900">Wipe votes for EVERY election</span>
                        </label>
                        <p className="text-[11px] text-slate-500 mt-0.5 pl-6">Original "wipe all" behaviour — chain drops to genesis, all vote_log rows removed.</p>
                    </div>

                    <div className="max-h-[52vh] overflow-y-auto px-5 py-3 bg-white" data-testid="wipe-votes-list">
                        {wipeElectionsLoading ? (
                            <div className="text-center py-10 text-sm text-slate-500 hash-mono">loading elections…</div>
                        ) : wipeElections.length === 0 ? (
                            <div className="text-center py-10 text-sm text-slate-500">No elections found.</div>
                        ) : (
                            <ul className="space-y-1.5">
                                {wipeElections.map((e) => {
                                    const votes = e.votes_count || 0;
                                    const checked = wipeSelected.has(e.id);
                                    return (
                                        <li key={e.id}>
                                            <label
                                                className={`flex items-center gap-2 px-2 py-2 rounded-md border cursor-pointer transition-colors ${wipeAll ? "opacity-50 cursor-not-allowed" : checked ? "border-amber-400 bg-amber-50" : "border-slate-200 hover:bg-slate-50"}`}
                                                data-testid={`wipe-elect-row-${e.id}`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    disabled={wipeAll}
                                                    onChange={() => toggleWipeElection(e.id)}
                                                    className="h-4 w-4 accent-amber-600 shrink-0"
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-semibold text-sm text-slate-900 truncate">{e.title}</div>
                                                    <div className="hash-mono text-[10px] text-slate-500 truncate">
                                                        {e.status?.toUpperCase()} · <span className="font-semibold text-slate-700">{votes}</span> vote{votes === 1 ? "" : "s"} on chain
                                                    </div>
                                                </div>
                                            </label>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>

                    <DialogFooter className="px-5 py-3 bg-slate-50 border-t border-slate-200">
                        <div className="text-[11px] text-slate-500 mr-auto">
                            {wipeAll
                                ? <span className="font-bold text-amber-800">ALL elections queued</span>
                                : wipeSelected.size === 0
                                    ? "Pick at least one election"
                                    : <>Selected <span className="font-bold text-slate-800">{wipeSelected.size}</span> of {wipeElections.length}</>}
                        </div>
                        <Button variant="outline" onClick={() => setWipeSelectOpen(false)}>Cancel</Button>
                        <Button
                            onClick={proceedWipeVotes}
                            disabled={!wipeAll && wipeSelected.size === 0}
                            className="bg-amber-600 hover:bg-amber-700 text-white disabled:bg-slate-300 disabled:text-slate-500"
                            data-testid="wipe-votes-continue"
                        >
                            Continue →
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={!!confirm} onOpenChange={(o) => { if (!o) { setConfirm(null); setConfirmText(""); } }}>
                <DialogContent data-testid="danger-confirm-dialog">
                    <DialogHeader>
                        <div className="flex items-center gap-2">
                            <Warning size={22} weight="fill" className="text-red-600" />
                            <DialogTitle className="font-heading text-2xl">{confirm?.title}</DialogTitle>
                        </div>
                        <DialogDescription>{confirm?.body}</DialogDescription>
                    </DialogHeader>
                    <div>
                        <Label className="overline text-slate-500">Type RESET to confirm</Label>
                        <Input
                            value={confirmText}
                            onChange={(e) => setConfirmText(e.target.value)}
                            placeholder="RESET"
                            className="font-mono mt-2"
                            data-testid="danger-confirm-input"
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setConfirm(null); setConfirmText(""); }}>Cancel</Button>
                        <Button
                            onClick={execute}
                            disabled={confirmText !== "RESET" || !!busy}
                            className="bg-red-600 hover:bg-red-700 text-white disabled:bg-slate-300 disabled:text-slate-500"
                            data-testid="danger-confirm-btn"
                        >
                            {busy ? "Wiping…" : "Confirm wipe"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function VoterEmailHistoryDialog({ voter, onClose }) {
    const [loading, setLoading] = useState(false);
    const [emails, setEmails] = useState([]);

    useEffect(() => {
        if (!voter) { setEmails([]); return; }
        setLoading(true);
        api.get(`/admin/voters/${voter.voter_id}/emails`)
            .then(({ data }) => setEmails(data?.emails || []))
            .catch(() => setEmails([]))
            .finally(() => setLoading(false));
    }, [voter]);

    if (!voter) return null;

    const KIND_META = {
        welcome:  { label: "Welcome",  color: "bg-civic-100 text-civic-800 border-civic-200" },
        reminder: { label: "Reminder", color: "bg-orange-100 text-orange-800 border-orange-200" },
        results:  { label: "Results",  color: "bg-purple-100 text-purple-800 border-purple-200" },
    };

    const fmt = (iso) => {
        if (!iso) return "—";
        try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
        catch { return iso.slice(0, 19).replace("T", " "); }
    };

    return (
        <Dialog open={!!voter} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="w-[calc(100vw-1rem)] sm:w-full sm:max-w-3xl max-h-[88vh] overflow-hidden p-0 gap-0" data-testid="voter-email-history-dialog">
                <div className="bg-civic-700 text-white px-5 py-3 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-widest text-civic-100 font-bold">Communications history</div>
                        <DialogTitle className="font-heading font-bold text-base sm:text-lg mt-0.5 truncate">{voter.name}</DialogTitle>
                        <DialogDescription className="text-white/85 text-[11px] mt-0.5 hash-mono">
                            {voter.voter_id} · {voter.email || "no email on file"}
                        </DialogDescription>
                    </div>
                </div>

                <div className="overflow-y-auto bg-white" style={{ maxHeight: "62vh" }} data-testid="voter-email-history-list">
                    {loading ? (
                        <div className="text-center py-10 text-sm text-slate-500 hash-mono">loading…</div>
                    ) : emails.length === 0 ? (
                        <div className="text-center py-10 px-6 text-sm text-slate-500">
                            <EnvelopeSimple size={32} weight="duotone" className="mx-auto text-slate-300 mb-2" />
                            No emails have been sent to this voter yet.
                            <div className="text-[11px] text-slate-400 mt-1">Use the W / R / R buttons on an election card to send a blast.</div>
                        </div>
                    ) : (
                        <Table className="text-sm">
                            <TableHeader>
                                <TableRow className="bg-slate-50 hover:bg-slate-50">
                                    <TableHead className="overline text-slate-500 w-[80px]">Type</TableHead>
                                    <TableHead className="overline text-slate-500">Election</TableHead>
                                    <TableHead className="overline text-slate-500">Subject</TableHead>
                                    <TableHead className="overline text-slate-500 whitespace-nowrap">Sent</TableHead>
                                    <TableHead className="overline text-slate-500 text-right w-[60px]">View</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {emails.map((m, i) => {
                                    const meta = KIND_META[m.kind] || { label: m.kind, color: "bg-slate-100 text-slate-700 border-slate-200" };
                                    const viewUrl = `${API}/email/view/${m.token}`;
                                    return (
                                        <TableRow key={m.token || i} data-testid={`voter-email-row-${i}`}>
                                            <TableCell>
                                                <span className={`inline-block text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border ${meta.color}`}>{meta.label}</span>
                                            </TableCell>
                                            <TableCell className="text-slate-700 text-sm">{m.election_title || <span className="hash-mono text-slate-400">{m.election_id?.slice(0, 8) || "—"}</span>}</TableCell>
                                            <TableCell className="text-slate-800 text-sm max-w-xs truncate" title={m.subject}>{m.subject}</TableCell>
                                            <TableCell className="hash-mono text-xs text-slate-500 whitespace-nowrap">{fmt(m.sent_at)}</TableCell>
                                            <TableCell className="text-right">
                                                <a href={viewUrl} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center h-8 w-8 rounded hover:bg-slate-100" title="Open rendered email">
                                                    <Eye size={15} weight="bold" className="text-civic-700" />
                                                </a>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}
                </div>

                <DialogFooter className="px-5 py-2.5 bg-slate-50 border-t border-slate-200">
                    <div className="text-[11px] text-slate-500 mr-auto">
                        {emails.length > 0 && <>Total: <strong className="text-slate-700">{emails.length}</strong> message{emails.length === 1 ? "" : "s"}</>}
                    </div>
                    <Button variant="outline" onClick={onClose} data-testid="voter-email-history-close">Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function ElectionEmailHistoryDialog({ election, onClose }) {
    const [loading, setLoading] = useState(false);
    const [emails, setEmails] = useState([]);
    const [stats, setStats] = useState({ eligible: 0, reached: 0, unreached: [] });
    const [kindFilter, setKindFilter] = useState("all");

    useEffect(() => {
        if (!election) { setEmails([]); setStats({ eligible: 0, reached: 0, unreached: [] }); setKindFilter("all"); return; }
        setLoading(true);
        api.get(`/admin/elections/${election.id}/emails`)
            .then(({ data }) => {
                setEmails(data?.emails || []);
                setStats({
                    eligible: data?.eligible_with_email || 0,
                    reached: data?.unique_reached || 0,
                    unreached: data?.unreached || [],
                });
            })
            .catch(() => { setEmails([]); setStats({ eligible: 0, reached: 0, unreached: [] }); })
            .finally(() => setLoading(false));
    }, [election]);

    if (!election) return null;

    const KIND_META = {
        welcome:  { label: "Welcome",  color: "bg-civic-100 text-civic-800 border-civic-200",   dot: "bg-civic-500" },
        reminder: { label: "Reminder", color: "bg-orange-100 text-orange-800 border-orange-200", dot: "bg-orange-500" },
        results:  { label: "Results",  color: "bg-purple-100 text-purple-800 border-purple-200", dot: "bg-purple-500" },
    };

    const fmt = (iso) => {
        if (!iso) return "—";
        try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
        catch { return iso.slice(0, 19).replace("T", " "); }
    };

    const filtered = kindFilter === "all" ? emails : emails.filter((m) => m.kind === kindFilter);
    const counts = emails.reduce((acc, m) => { acc[m.kind] = (acc[m.kind] || 0) + 1; return acc; }, {});

    return (
        <Dialog open={!!election} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="w-[calc(100vw-1rem)] sm:w-full sm:max-w-4xl max-h-[88vh] overflow-hidden p-0 gap-0" data-testid="election-email-history-dialog">
                <div className="bg-civic-700 text-white px-5 py-3">
                    <div className="text-[10px] uppercase tracking-widest text-civic-100 font-bold flex items-center gap-1.5">
                        <ClockCounterClockwise size={11} weight="bold" /> Election email history
                    </div>
                    <DialogTitle className="font-heading font-bold text-base sm:text-lg mt-0.5 truncate">{election.title}</DialogTitle>
                    <DialogDescription className="text-white/85 text-[11px] mt-0.5 hash-mono">
                        id: {election.id?.slice(0, 8)} · {emails.length} message{emails.length === 1 ? "" : "s"} sent
                    </DialogDescription>
                </div>

                {/* Filter pills */}
                <div className="px-5 py-2 bg-slate-50 border-b border-slate-200 flex items-center gap-1.5 text-[11px]" data-testid="election-email-history-filters">
                    <span className="text-slate-500 font-semibold uppercase tracking-wider text-[10px] mr-1">Filter</span>
                    {["all", "welcome", "reminder", "results"].map((k) => {
                        const active = kindFilter === k;
                        const label = k === "all" ? "All" : KIND_META[k]?.label || k;
                        const n = k === "all" ? emails.length : (counts[k] || 0);
                        return (
                            <button
                                key={k}
                                type="button"
                                onClick={() => setKindFilter(k)}
                                className={`px-2 py-0.5 rounded-full border transition-colors ${active
                                    ? "bg-civic-600 text-white border-civic-600"
                                    : "bg-white text-slate-700 border-slate-200 hover:border-civic-300"}`}
                                data-testid={`election-email-filter-${k}`}
                            >
                                {label} <span className={`hash-mono ml-1 ${active ? "text-civic-100" : "text-slate-400"}`}>{n}</span>
                            </button>
                        );
                    })}
                </div>

                {/* Reconciliation strip — surfaces the "preview said N but only M
                    were reached" gap so the admin knows which voters to fix. */}
                {emails.length > 0 && stats.unreached.length > 0 && (
                    <div className="px-5 py-3 bg-amber-50 border-b border-amber-200" data-testid="election-email-reconciliation">
                        <div className="flex items-start gap-2">
                            <Warning size={18} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
                            <div className="text-[12px] text-amber-900 leading-snug">
                                <div className="font-bold">
                                    Reached {stats.reached} of {stats.eligible} eligible voter{stats.eligible === 1 ? "" : "s"} with email
                                </div>
                                <div className="text-amber-800/90 mt-0.5">
                                    The following {stats.unreached.length === 1 ? "voter is" : `${stats.unreached.length} voters are`} eligible but never received a successful send — likely a bad or malformed email address on file:
                                </div>
                                <ul className="mt-1.5 space-y-0.5">
                                    {stats.unreached.map((u) => (
                                        <li key={u.voter_id} className="flex items-center gap-2 flex-wrap" data-testid={`unreached-${u.voter_id}`}>
                                            <span className="font-semibold text-amber-950">{u.name || u.voter_id}</span>
                                            <span className="hash-mono text-[10px] text-amber-700">{u.voter_id}</span>
                                            <span className="hash-mono text-amber-800 text-[11px]">{u.email || "(no email)"}</span>
                                            {!u.email_looks_valid && u.email && (
                                                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-rose-100 text-rose-800 border border-rose-200 rounded px-1.5 py-0.5">
                                                    <Warning size={10} weight="fill" /> Invalid format
                                                </span>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                )}

                <div className="overflow-y-auto bg-white" style={{ maxHeight: "60vh" }} data-testid="election-email-history-list">
                    {loading ? (
                        <div className="text-center py-10 text-sm text-slate-500 hash-mono">loading…</div>
                    ) : filtered.length === 0 ? (
                        <div className="text-center py-10 px-6 text-sm text-slate-500">
                            <EnvelopeSimple size={32} weight="duotone" className="mx-auto text-slate-300 mb-2" />
                            {emails.length === 0
                                ? "No emails have been sent for this election yet."
                                : "No emails match this filter."}
                            {emails.length === 0 && (
                                <div className="text-[11px] text-slate-400 mt-1">Use the W / R / R buttons on this election card to send a blast.</div>
                            )}
                        </div>
                    ) : (
                        <Table className="text-sm">
                            <TableHeader>
                                <TableRow className="bg-slate-50 hover:bg-slate-50">
                                    <TableHead className="overline text-slate-500 w-[80px]">Type</TableHead>
                                    <TableHead className="overline text-slate-500">Recipient</TableHead>
                                    <TableHead className="overline text-slate-500">Subject</TableHead>
                                    <TableHead className="overline text-slate-500 whitespace-nowrap">Sent</TableHead>
                                    <TableHead className="overline text-slate-500 text-right w-[60px]">View</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filtered.map((m, i) => {
                                    const meta = KIND_META[m.kind] || { label: m.kind, color: "bg-slate-100 text-slate-700 border-slate-200" };
                                    const viewUrl = `${API}/email/view/${m.token}`;
                                    return (
                                        <TableRow key={m.token || i} data-testid={`election-email-row-${i}`}>
                                            <TableCell>
                                                <span className={`inline-block text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border ${meta.color}`}>{meta.label}</span>
                                            </TableCell>
                                            <TableCell className="text-slate-800 text-sm">
                                                <div className="font-semibold truncate max-w-[220px]" title={m.voter_name || ""}>
                                                    {m.voter_name || <span className="italic text-slate-400 font-normal">Unknown</span>}
                                                </div>
                                                <div className="hash-mono text-[10px] text-slate-500 truncate max-w-[220px]">
                                                    {m.voter_id_display && <>{m.voter_id_display} · </>}
                                                    {m.to_email || "—"}
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-slate-800 text-sm max-w-xs truncate" title={m.subject}>{m.subject}</TableCell>
                                            <TableCell className="hash-mono text-xs text-slate-500 whitespace-nowrap">{fmt(m.sent_at)}</TableCell>
                                            <TableCell className="text-right">
                                                <a href={viewUrl} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center h-8 w-8 rounded hover:bg-slate-100" title="Open rendered email">
                                                    <Eye size={15} weight="bold" className="text-civic-700" />
                                                </a>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}
                </div>

                <DialogFooter className="px-5 py-2.5 bg-slate-50 border-t border-slate-200">
                    <div className="text-[11px] text-slate-500 mr-auto">
                        {emails.length > 0 && (
                            <>Showing <strong className="text-slate-700">{filtered.length}</strong> of {emails.length}</>
                        )}
                    </div>
                    <Button variant="outline" onClick={onClose} data-testid="election-email-history-close">Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function SalesLicensingPanel() {
    const [loaded, setLoaded] = useState(false);
    const [busy, setBusy] = useState(false);
    const [email, setEmail] = useState("");
    const [savedEmail, setSavedEmail] = useState("");

    // Inquiry inbox state
    const [inquiries, setInquiries] = useState([]);
    const [counts, setCounts] = useState({ new: 0, handled: 0, archived: 0, total: 0 });
    const [statusFilter, setStatusFilter] = useState("new");
    const [expandedId, setExpandedId] = useState(null);

    const loadSettings = async () => {
        try {
            const { data } = await api.get("/admin/settings");
            setEmail(data.sales_manager_email || "");
            setSavedEmail(data.sales_manager_email || "");
        } catch (e) { toast.error(e.response?.data?.detail || "Could not load settings"); }
    };
    const loadInquiries = async (status = statusFilter) => {
        try {
            const params = status ? { status } : {};
            const { data } = await api.get("/admin/sales-inquiries", { params });
            setInquiries(data.items || []);
            setCounts(data.counts || { new: 0, handled: 0, archived: 0, total: 0 });
        } catch (e) { toast.error(e.response?.data?.detail || "Could not load inquiries"); }
    };
    useEffect(() => {
        (async () => {
            await Promise.all([loadSettings(), loadInquiries("new")]);
            setLoaded(true);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const save = async () => {
        setBusy(true);
        try {
            await api.put("/admin/settings", { sales_manager_email: email.trim() });
            const clean = email.trim();
            setEmail(clean);
            setSavedEmail(clean);
            toast.success(clean
                ? `Sales manager email saved · ${clean}`
                : "Sales manager email cleared");
        } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
        setBusy(false);
    };

    const changeStatus = async (row, next) => {
        try {
            await api.patch(`/admin/sales-inquiries/${row.id}/status`, { status: next });
            toast.success(`Marked as ${next}`);
            await loadInquiries();
        } catch (e) { toast.error(e.response?.data?.detail || "Could not update status"); }
    };
    const deleteInquiry = async (row) => {
        if (!window.confirm(`Delete inquiry from "${row.name}" <${row.email}>?\n\nThis cannot be undone.`)) return;
        try {
            await api.delete(`/admin/sales-inquiries/${row.id}`);
            toast.success("Inquiry deleted");
            await loadInquiries();
        } catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
    };
    const applyFilter = async (next) => {
        setStatusFilter(next);
        await loadInquiries(next);
    };

    const dirty = email.trim() !== savedEmail;
    const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

    return (
        <div className="space-y-4" data-testid="sales-licensing-panel">
            <Card className="p-6 bg-white border-slate-200">
                <div className="flex items-start gap-3">
                    <div className="h-10 w-10 grid place-items-center rounded-md bg-civic-50 border border-civic-100 shrink-0">
                        <EnvelopeSimple size={20} weight="duotone" className="text-civic-700" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h2 className="font-heading font-bold text-lg text-slate-900">Sales / Licensing</h2>
                        <p className="text-sm text-slate-600 mt-1 max-w-2xl">
                            Point of contact for licensing questions, renewals, and commercial inquiries.
                            This address will surface in the admin console and receive every submission from the public Sales inquiry form.
                        </p>
                    </div>
                </div>

                <div className="mt-6 max-w-lg space-y-2">
                    <label htmlFor="sales-manager-email" className="overline text-slate-500 block">Sales manager email</label>
                    <Input
                        id="sales-manager-email"
                        type="email"
                        value={email}
                        onChange={(ev) => setEmail(ev.target.value)}
                        placeholder="sales@your-organisation.com"
                        className="h-10"
                        disabled={!loaded || busy}
                        data-testid="sales-manager-email-input"
                    />
                    {savedEmail && (
                        <p className="text-[11px] text-slate-500 hash-mono">
                            Currently on file:{" "}
                            <a href={`mailto:${savedEmail}`} className="text-civic-700 underline-offset-2 hover:underline" data-testid="sales-manager-email-mailto">
                                {savedEmail}
                            </a>
                        </p>
                    )}
                    <div className="flex items-center gap-2 pt-2">
                        <Button
                            onClick={save}
                            disabled={!loaded || busy || !dirty}
                            className="bg-civic-600 hover:bg-civic-700 text-white"
                            data-testid="sales-manager-email-save"
                        >
                            {busy ? "Saving…" : dirty ? "Save changes" : "Saved"}
                        </Button>
                        {dirty && (
                            <Button
                                variant="outline"
                                onClick={() => setEmail(savedEmail)}
                                disabled={busy}
                                data-testid="sales-manager-email-reset"
                            >
                                Reset
                            </Button>
                        )}
                    </div>
                </div>
            </Card>

            {/* ─── Inquiry Inbox ─────────────────────────────────────────────── */}
            <Card className="p-6 bg-white border-slate-200" data-testid="sales-inquiry-inbox">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                        <h3 className="font-heading font-bold text-lg text-slate-900">Inquiry inbox</h3>
                        <p className="text-xs text-slate-500 mt-1">
                            Every submission from the public "Inquire about Sales / Licensing" form. Rate-limited to 3 per hour per IP, with an invisible honeypot to catch bots.
                        </p>
                    </div>
                    <div className="flex items-center gap-1 flex-wrap">
                        {[
                            { key: "new",      label: `New · ${counts.new}`,           tone: "bg-civic-600 text-white" },
                            { key: "handled",  label: `Handled · ${counts.handled}`,   tone: "bg-emerald-600 text-white" },
                            { key: "archived", label: `Archived · ${counts.archived}`, tone: "bg-slate-500 text-white" },
                            { key: "",         label: `All · ${counts.total}`,         tone: "bg-slate-700 text-white" },
                        ].map((chip) => (
                            <button
                                key={chip.key || "all"}
                                type="button"
                                onClick={() => applyFilter(chip.key)}
                                className={`h-7 px-2.5 text-[11px] hash-mono rounded-md border transition-colors ${statusFilter === chip.key ? chip.tone + " border-transparent" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
                                data-testid={`inquiry-filter-${chip.key || "all"}`}
                            >
                                {chip.label}
                            </button>
                        ))}
                        <Button size="sm" variant="outline" className="h-7 text-[11px] hash-mono" onClick={() => loadInquiries()} data-testid="inquiry-refresh">Refresh</Button>
                    </div>
                </div>

                {inquiries.length === 0 ? (
                    <div className="mt-6 text-center py-10 border border-dashed border-slate-200 rounded-md text-slate-500 text-sm" data-testid="inquiry-empty">
                        No {statusFilter || "matching"} inquiries yet.
                    </div>
                ) : (
                    <div className="mt-4 divide-y divide-slate-100 border border-slate-200 rounded-md overflow-hidden">
                        {inquiries.map((row) => {
                            const isOpen = expandedId === row.id;
                            const undelivered = row.delivered === false;
                            return (
                                <div key={row.id} className="bg-white" data-testid={`inquiry-row-${row.id}`}>
                                    <button
                                        type="button"
                                        onClick={() => setExpandedId(isOpen ? null : row.id)}
                                        className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-start gap-3"
                                        data-testid={`inquiry-expand-${row.id}`}
                                    >
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-semibold text-slate-900">{row.name}</span>
                                                {row.organization && <span className="text-xs text-slate-500">· {row.organization}</span>}
                                                {row.status === "handled" && <Badge className="text-[10px] h-5 px-1.5 bg-emerald-100 text-emerald-800 border border-emerald-200">HANDLED</Badge>}
                                                {row.status === "archived" && <Badge className="text-[10px] h-5 px-1.5 bg-slate-200 text-slate-700 border border-slate-300">ARCHIVED</Badge>}
                                                {undelivered && <Badge className="text-[10px] h-5 px-1.5 bg-rose-100 text-rose-800 border border-rose-200">EMAIL FAILED</Badge>}
                                            </div>
                                            <div className="text-xs text-slate-500 mt-0.5 hash-mono truncate">
                                                {row.email}{row.phone ? ` · ${row.phone}` : ""} · {fmtDate(row.created_at)}
                                            </div>
                                            {!isOpen && (
                                                <div className="text-xs text-slate-600 mt-1 line-clamp-2 max-w-3xl">{row.message}</div>
                                            )}
                                        </div>
                                        <CaretRight size={16} className={`text-slate-400 shrink-0 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                                    </button>
                                    {isOpen && (
                                        <div className="px-4 pb-4 pt-1 bg-slate-50 border-t border-slate-100 space-y-3">
                                            <div className="grid sm:grid-cols-2 gap-2 text-xs">
                                                <div><span className="overline text-slate-500 mr-2">Email</span><a href={`mailto:${row.email}`} className="hash-mono text-civic-700 underline-offset-2 hover:underline">{row.email}</a></div>
                                                <div><span className="overline text-slate-500 mr-2">Phone</span><span className="hash-mono text-slate-700">{row.phone || "—"}</span></div>
                                                <div><span className="overline text-slate-500 mr-2">Org</span><span className="text-slate-700">{row.organization || "—"}</span></div>
                                                <div><span className="overline text-slate-500 mr-2">IP</span><span className="hash-mono text-slate-700">{row.ip || "—"}</span></div>
                                            </div>
                                            {undelivered && row.delivery_error && (
                                                <div className="p-2 rounded bg-rose-50 border border-rose-200 text-[11px] text-rose-800 hash-mono">
                                                    Delivery error: {row.delivery_error}
                                                </div>
                                            )}
                                            <div>
                                                <div className="overline text-slate-500 mb-1">Message</div>
                                                <pre className="text-xs text-slate-800 whitespace-pre-wrap break-words bg-white p-3 rounded border border-slate-200 max-h-64 overflow-y-auto">{row.message}</pre>
                                            </div>
                                            <div className="flex items-center gap-2 flex-wrap pt-1">
                                                <a href={`mailto:${row.email}?subject=Re:%20Your%20E-VoteVerify%20inquiry`}>
                                                    <Button size="sm" className="bg-civic-600 hover:bg-civic-700 text-white h-7 text-[11px]" data-testid={`inquiry-reply-${row.id}`}>
                                                        <EnvelopeSimple size={11} weight="fill" className="mr-1" /> Reply
                                                    </Button>
                                                </a>
                                                {row.status !== "handled" && (
                                                    <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => changeStatus(row, "handled")} data-testid={`inquiry-mark-handled-${row.id}`}>
                                                        Mark handled
                                                    </Button>
                                                )}
                                                {row.status !== "new" && (
                                                    <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => changeStatus(row, "new")} data-testid={`inquiry-mark-new-${row.id}`}>
                                                        Reopen
                                                    </Button>
                                                )}
                                                {row.status !== "archived" && (
                                                    <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => changeStatus(row, "archived")} data-testid={`inquiry-archive-${row.id}`}>
                                                        Archive
                                                    </Button>
                                                )}
                                                <Button size="sm" variant="outline" className="h-7 text-[11px] text-rose-700 border-rose-200 hover:bg-rose-50 ml-auto" onClick={() => deleteInquiry(row)} data-testid={`inquiry-delete-${row.id}`}>
                                                    Delete
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </Card>
        </div>
    );
}

function SessionTimeoutPanel({ onSaved }) {
    const { refresh } = useAuth();
    const [loaded, setLoaded] = useState(false);
    const [busy, setBusy] = useState(false);
    const [adminMin, setAdminMin] = useState(30);
    const [voterMin, setVoterMin] = useState(30);
    const [savedAdmin, setSavedAdmin] = useState(30);
    const [savedVoter, setSavedVoter] = useState(30);

    const load = async () => {
        try {
            const { data } = await api.get("/admin/settings");
            const a = Number(data.idle_timeout_admin_minutes) > 0 ? Number(data.idle_timeout_admin_minutes) : 30;
            const v = Number(data.idle_timeout_voter_minutes) > 0 ? Number(data.idle_timeout_voter_minutes) : 30;
            setAdminMin(a); setSavedAdmin(a);
            setVoterMin(v); setSavedVoter(v);
        } catch (e) { toast.error(e.response?.data?.detail || "Could not load timeout settings"); }
        setLoaded(true);
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        setBusy(true);
        try {
            await api.put("/admin/settings", {
                idle_timeout_admin_minutes: Number(adminMin),
                idle_timeout_voter_minutes: Number(voterMin),
            });
            setSavedAdmin(Number(adminMin));
            setSavedVoter(Number(voterMin));
            toast.success("Idle-timeout window saved");
            // Re-hydrate the current session so IdleTimeout + SessionMonitor
            // pick up the new value without a page reload.
            try { await refresh(); } catch { /* silent */ }
            if (onSaved) onSaved();
        } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
        setBusy(false);
    };

    const dirty = Number(adminMin) !== savedAdmin || Number(voterMin) !== savedVoter;
    if (!loaded) return <div className="hash-mono text-slate-500 text-sm">loading…</div>;

    return (
        <Card className="p-5 bg-white border-slate-200" data-testid="settings-idle-timeout">
            <div className="flex items-start gap-3">
                <div className="h-10 w-10 grid place-items-center rounded-md bg-amber-50 border border-amber-200 shrink-0">
                    <Timer size={20} weight="duotone" className="text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="font-heading font-bold text-lg text-slate-900">Idle-session timeout</h3>
                    <p className="text-xs text-slate-500 mt-1 max-w-2xl">
                        How long a signed-in user can be inactive before the app force-logs them out. The backend JWT expiry is matched to this value at login, so a stale token can't outlive the idle timeout on the server.
                    </p>
                </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mt-5">
                <div>
                    <label htmlFor="idle-admin-min" className="overline text-slate-500 block mb-1">Admin idle window (minutes)</label>
                    <Input
                        id="idle-admin-min"
                        type="number"
                        min={1}
                        max={1440}
                        value={adminMin}
                        onChange={(e) => setAdminMin(e.target.value)}
                        className="h-10 hash-mono max-w-[10rem]"
                        data-testid="idle-admin-input"
                    />
                    <p className="text-[10px] text-slate-500 mt-1">Suggested: 30–60 for admin consoles.</p>
                </div>
                <div>
                    <label htmlFor="idle-voter-min" className="overline text-slate-500 block mb-1">Voter idle window (minutes)</label>
                    <Input
                        id="idle-voter-min"
                        type="number"
                        min={1}
                        max={1440}
                        value={voterMin}
                        onChange={(e) => setVoterMin(e.target.value)}
                        className="h-10 hash-mono max-w-[10rem]"
                        data-testid="idle-voter-input"
                    />
                    <p className="text-[10px] text-slate-500 mt-1">Suggested: 15–30 while a ballot is mid-flight.</p>
                </div>
            </div>

            <div className="flex items-center gap-2 mt-5">
                <Button
                    onClick={save}
                    disabled={busy || !dirty}
                    className="bg-civic-600 hover:bg-civic-700 text-white"
                    data-testid="idle-timeout-save"
                >
                    {busy ? "Saving…" : dirty ? "Save changes" : "Saved"}
                </Button>
                {dirty && (
                    <Button variant="outline" onClick={() => { setAdminMin(savedAdmin); setVoterMin(savedVoter); }} disabled={busy}>
                        Reset
                    </Button>
                )}
            </div>
        </Card>
    );
}

function SettingsPanel() {
    const [loaded, setLoaded] = useState(false);
    const [busy, setBusy] = useState(false);
    const [footerText, setFooterText] = useState("");
    const [supportEmail, setSupportEmail] = useState("");
    const [showIpBadge, setShowIpBadge] = useState(true);
    const [links, setLinks] = useState([]); // [{label, url}]

    const load = async () => {
        try {
            const { data } = await api.get("/settings");
            setFooterText(data.footer_text || "");
            setSupportEmail(data.support_email || "");
            setShowIpBadge(data.show_ip_badge !== false);
            setLinks(Array.isArray(data.footer_links) ? data.footer_links : []);
        } catch {/* swallow */}
        setLoaded(true);
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        setBusy(true);
        try {
            await api.put("/admin/settings", {
                footer_text: footerText,
                support_email: supportEmail,
                show_ip_badge: showIpBadge,
                footer_links: links.filter((l) => l.label && l.url),
            });
            toast.success("Site settings saved — footer updated everywhere");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not save settings");
        }
        setBusy(false);
    };

    const addLink = () => setLinks([...links, { label: "", url: "" }]);
    const setLink = (i, k, v) => setLinks(links.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
    const rmLink = (i) => setLinks(links.filter((_, j) => j !== i));

    if (!loaded) return <div className="hash-mono text-slate-500 text-sm">loading…</div>;

    return (
        <div className="space-y-5" data-testid="settings-panel">
            <SessionTimeoutPanel />
            <SmtpPanel />
            <BackupPanel />
            <div className="grid lg:grid-cols-3 gap-5">
            <Card className="p-5 lg:col-span-2 bg-white border-slate-200">
                <h3 className="font-heading font-bold text-lg text-slate-900">Footer text</h3>
                <p className="text-xs text-slate-500 mt-1">Shown at the bottom of every public page (landing, results, explorer, voter pages, admin).</p>
                <Textarea
                    value={footerText}
                    onChange={(e) => setFooterText(e.target.value)}
                    rows={3}
                    placeholder="© 2026 Denton County Elections — voter-anonymous, hash-chained."
                    className="mt-3 hash-mono text-sm"
                    data-testid="settings-footer-text"
                />

                <div className="mt-5">
                    <div className="flex items-center justify-between">
                        <h4 className="font-semibold text-sm text-slate-900">Footer links</h4>
                        <Button type="button" size="sm" variant="outline" onClick={addLink} data-testid="settings-add-link">
                            <Plus size={14} className="mr-1.5" /> Add link
                        </Button>
                    </div>
                    <div className="mt-2 space-y-2">
                        {links.length === 0 && <div className="text-xs text-slate-400 italic">No links yet — add a "Privacy Policy" or "Election Authority" link.</div>}
                        {links.map((l, i) => (
                            <div key={i} className="grid grid-cols-12 gap-2" data-testid={`settings-link-${i}`}>
                                <Input className="col-span-4 h-9 text-sm" placeholder="Label" value={l.label} onChange={(e) => setLink(i, "label", e.target.value)} data-testid={`settings-link-label-${i}`} />
                                <Input className="col-span-7 h-9 text-sm font-mono" placeholder="https://…" value={l.url} onChange={(e) => setLink(i, "url", e.target.value)} data-testid={`settings-link-url-${i}`} />
                                <Button type="button" size="icon" variant="ghost" onClick={() => rmLink(i)} className="col-span-1 h-9 w-9" data-testid={`settings-link-remove-${i}`}><X size={14} /></Button>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="mt-5 grid sm:grid-cols-2 gap-3">
                    <div>
                        <Label className="text-xs">Support email</Label>
                        <Input
                            type="email"
                            value={supportEmail}
                            onChange={(e) => setSupportEmail(e.target.value)}
                            placeholder="elections@example.gov"
                            className="mt-1 h-9 text-sm"
                            data-testid="settings-support-email"
                        />
                    </div>
                    <label className="flex items-end gap-2 mb-1">
                        <Checkbox
                            checked={showIpBadge}
                            onCheckedChange={(v) => setShowIpBadge(!!v)}
                            data-testid="settings-show-ip-badge"
                        />
                        <span className="text-xs text-slate-700 leading-snug pb-0.5">Show <span className="font-semibold">IP address &amp; VPN flag</span> on the footer of every page</span>
                    </label>
                </div>

                <div className="mt-6 flex gap-2">
                    <Button onClick={save} disabled={busy} className="bg-civic-600 hover:bg-civic-700 text-white" data-testid="settings-save-btn">
                        {busy ? "Saving…" : "Save settings"}
                    </Button>
                </div>
            </Card>

            <Card className="p-5 bg-slate-50 border-slate-200">
                <div className="overline text-civic-700">Live preview</div>
                <h3 className="font-heading font-bold text-base text-slate-900 mt-1">What voters & observers see</h3>
                <div className="mt-3 rounded-md border border-slate-200 bg-white p-3" data-testid="settings-preview">
                    <div className="text-xs text-slate-700 whitespace-pre-wrap">{footerText || <span className="italic text-slate-400">Footer is empty.</span>}</div>
                    {(links.length > 0 || supportEmail) && (
                        <div className="mt-2 flex flex-wrap gap-x-3 text-xs">
                            {links.filter((l) => l.label && l.url).map((l, i) => (
                                <a key={i} href={l.url} target="_blank" rel="noreferrer" className="text-civic-600 hover:underline">{l.label}</a>
                            ))}
                            {supportEmail && <a href={`mailto:${supportEmail}`} className="text-civic-600 hover:underline">{supportEmail}</a>}
                        </div>
                    )}
                </div>
                <p className="text-[11px] text-slate-500 mt-3 leading-snug">
                    The IP &amp; VPN badge is added on the right side of the footer when enabled. VPN/proxy detection uses ip-api.com (free tier).
                </p>
            </Card>
            </div>
        </div>
    );
}

function SmtpPanel() {
    const [open, setOpen] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [busy, setBusy] = useState(false);
    const [testing, setTesting] = useState(false);
    const [showPw, setShowPw] = useState(false);

    const [host, setHost] = useState("");
    const [port, setPort] = useState(587);
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [pwSet, setPwSet] = useState(false);
    const [fromEmail, setFromEmail] = useState("");
    const [fromName, setFromName] = useState("E-VoteVerify");
    const [useTls, setUseTls] = useState(true);
    const [testTo, setTestTo] = useState("");

    const load = async () => {
        try {
            const { data } = await api.get("/admin/settings");
            setHost(data.smtp_host || "");
            setPort(data.smtp_port || 587);
            setUsername(data.smtp_username || "");
            setPwSet(!!data.smtp_password_set);
            setPassword("");
            setFromEmail(data.smtp_from_email || "");
            setFromName(data.smtp_from_name || "E-VoteVerify");
            setUseTls(data.smtp_use_tls !== false);
        } catch {/* swallow */}
        setLoaded(true);
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        setBusy(true);
        try {
            await api.put("/admin/settings", {
                smtp_host: host.trim(),
                smtp_port: Number(port) || 587,
                smtp_username: username.trim(),
                smtp_password: password, // empty = keep existing
                smtp_from_email: fromEmail.trim(),
                smtp_from_name: fromName.trim(),
                smtp_use_tls: !!useTls,
            });
            toast.success("SMTP settings saved");
            setPassword("");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not save SMTP settings");
        }
        setBusy(false);
    };

    // Persist form values silently (no toast). Used by Send Test.
    const persistSilently = async () => {
        await api.put("/admin/settings", {
            smtp_host: host.trim(),
            smtp_port: Number(port) || 587,
            smtp_username: username.trim(),
            smtp_password: password, // empty = keep existing
            smtp_from_email: fromEmail.trim(),
            smtp_from_name: fromName.trim(),
            smtp_use_tls: !!useTls,
        });
    };

    const sendTest = async () => {
        if (!testTo) { toast.error("Enter a recipient email for the test"); return; }
        if (!host.trim() || !(fromEmail.trim() || username.trim())) {
            toast.error("Fill in SMTP Host and From Email (or Username) before testing");
            return;
        }
        setTesting(true);
        let saved = false;
        try {
            // Auto-save current form values so the test uses what's on screen.
            await persistSilently();
            saved = true;
            setPassword("");
            await api.post("/admin/settings/smtp/test", { to_email: testTo });
            toast.success(`Test email sent to ${testTo}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || (saved ? "SMTP test failed" : "Could not save SMTP settings"));
        } finally {
            // Always refresh so the saved-state indicator (e.g., "Password saved") reflects DB.
            await load();
        }
        setTesting(false);
    };

    return (
        <Card className="bg-white border-slate-200 overflow-hidden" data-testid="smtp-panel">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="w-full flex items-center justify-between gap-3 px-5 py-4 hover:bg-slate-50 transition-colors"
                aria-expanded={open}
                data-testid="smtp-panel-toggle"
            >
                <div className="flex items-center gap-3 min-w-0">
                    <div className="h-9 w-9 grid place-items-center rounded-md bg-civic-50 border border-civic-100">
                        <EnvelopeSimple size={18} weight="duotone" className="text-civic-700" />
                    </div>
                    <div className="text-left min-w-0">
                        <h3 className="font-heading font-bold text-base text-slate-900">SMTP Email Settings</h3>
                        <p className="text-xs text-slate-500 truncate">
                            {host ? <>Configured: <span className="hash-mono">{host}:{port}</span> {pwSet && <span className="text-emerald-700 ml-1">· auth set</span>}</> : "Not configured — emails are disabled"}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    {host ? (
                        <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 hash-mono text-[10px]">Configured</Badge>
                    ) : (
                        <Badge className="bg-slate-100 text-slate-600 border border-slate-200 hash-mono text-[10px]">Off</Badge>
                    )}
                    {open ? <CaretUp size={18} weight="bold" className="text-slate-500" /> : <CaretDown size={18} weight="bold" className="text-slate-500" />}
                </div>
            </button>

            {open && loaded && (
                <div className="px-5 pb-5 pt-1 border-t border-slate-100" data-testid="smtp-panel-body">
                    <div className="grid sm:grid-cols-2 gap-4 mt-4">
                        <div>
                            <Label className="overline text-slate-500">SMTP Host</Label>
                            <Input
                                className="mt-1.5 h-10 font-mono text-sm"
                                value={host}
                                onChange={(e) => setHost(e.target.value)}
                                placeholder="smtp.mail.com"
                                data-testid="smtp-host"
                            />
                        </div>
                        <div>
                            <Label className="overline text-slate-500">SMTP Port</Label>
                            <Input
                                className="mt-1.5 h-10 font-mono text-sm"
                                type="number"
                                value={port}
                                onChange={(e) => setPort(e.target.value)}
                                placeholder="587"
                                data-testid="smtp-port"
                            />
                        </div>
                        <div>
                            <Label className="overline text-slate-500">SMTP Email / Username</Label>
                            <Input
                                className="mt-1.5 h-10 text-sm"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                placeholder="E-Vote-Verify@mail.com"
                                data-testid="smtp-username"
                            />
                        </div>
                        <div>
                            <Label className="overline text-slate-500 flex items-center gap-2">
                                SMTP Password
                                {pwSet && password === "" && (
                                    <>
                                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 hash-mono normal-case tracking-normal" data-testid="smtp-password-saved-badge">
                                            <CheckCircle size={10} weight="fill" /> Saved
                                        </span>
                                        <button
                                            type="button"
                                            onClick={async () => {
                                                try {
                                                    const { data } = await api.get("/admin/settings/smtp/password");
                                                    if (data?.smtp_password) {
                                                        setPassword(data.smtp_password);
                                                        setShowPw(true);
                                                    } else {
                                                        toast.info("No password saved yet");
                                                    }
                                                } catch {
                                                    toast.error("Could not reveal saved password");
                                                }
                                            }}
                                            className="text-[10px] font-bold text-civic-700 hover:text-civic-900 hover:underline normal-case tracking-normal"
                                            data-testid="smtp-password-reveal"
                                        >
                                            Reveal saved
                                        </button>
                                    </>
                                )}
                            </Label>
                            <div className="relative mt-1.5">
                                <Input
                                    className="h-10 text-sm pr-10 font-mono"
                                    type={showPw ? "text" : "password"}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder={pwSet ? "Password saved — leave blank to keep, or type a new one to replace" : "Enter SMTP password"}
                                    autoComplete="new-password"
                                    data-testid="smtp-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPw((v) => !v)}
                                    aria-label={showPw ? "Hide password" : "Show password"}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-slate-500 hover:text-civic-700 hover:bg-slate-100"
                                    data-testid="smtp-password-toggle"
                                >
                                    {showPw ? <EyeSlash size={16} weight="bold" /> : <Eye size={16} weight="bold" />}
                                </button>
                            </div>
                        </div>
                        <div>
                            <Label className="overline text-slate-500">From Email Address</Label>
                            <Input
                                className="mt-1.5 h-10 text-sm"
                                type="email"
                                value={fromEmail}
                                onChange={(e) => setFromEmail(e.target.value)}
                                placeholder="E-Vote-Verify@mail.com"
                                data-testid="smtp-from-email"
                            />
                        </div>
                        <div>
                            <Label className="overline text-slate-500">From Display Name</Label>
                            <Input
                                className="mt-1.5 h-10 text-sm"
                                value={fromName}
                                onChange={(e) => setFromName(e.target.value)}
                                placeholder="E-Vote Verify System"
                                data-testid="smtp-from-name"
                            />
                        </div>
                    </div>

                    <label className="mt-4 flex items-center gap-2 text-xs text-slate-700 select-none">
                        <Checkbox
                            checked={useTls}
                            onCheckedChange={(v) => setUseTls(!!v)}
                            data-testid="smtp-use-tls"
                        />
                        Use STARTTLS (recommended for port 587). Port 465 uses implicit SSL automatically.
                    </label>

                    <div className="mt-5 pt-4 border-t border-slate-100">
                        <Label className="overline text-slate-500">Test Email Settings</Label>
                        <div className="mt-1.5 flex gap-2">
                            <Input
                                className="flex-1 h-10 text-sm"
                                type="email"
                                value={testTo}
                                onChange={(e) => setTestTo(e.target.value)}
                                placeholder="Enter email address to receive test"
                                data-testid="smtp-test-to"
                            />
                            <Button
                                type="button"
                                variant="outline"
                                onClick={sendTest}
                                disabled={testing}
                                className="h-10 shrink-0"
                                data-testid="smtp-test-send"
                            >
                                <PaperPlaneTilt size={14} className="mr-1.5" weight="bold" />
                                {testing ? "Sending…" : "Send Test"}
                            </Button>
                        </div>
                        <p className="text-[11px] text-slate-500 mt-1.5">Send Test will automatically save your current settings first, then send a verification email.</p>
                    </div>

                    <div className="mt-5 flex justify-end">
                        <Button
                            type="button"
                            onClick={save}
                            disabled={busy}
                            className="bg-civic-600 hover:bg-civic-700 text-white"
                            data-testid="smtp-save"
                        >
                            {busy ? "Saving…" : "Save SMTP Settings"}
                        </Button>
                    </div>
                </div>
            )}
        </Card>
    );
}

function fmtIso(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
    } catch { return iso; }
}

function BackupPanel() {
    const [open, setOpen] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [busy, setBusy] = useState(false);
    const [running, setRunning] = useState(false);
    const [sending, setSending] = useState(false);
    const [restoring, setRestoring] = useState(false);

    const [enabled, setEnabled] = useState(false);
    const [frequency, setFrequency] = useState("weekly");
    const [day, setDay] = useState("sunday");
    const [time, setTime] = useState("23:59");
    const [email, setEmail] = useState("");
    const [lastAt, setLastAt] = useState(null);
    const [nextAt, setNextAt] = useState(null);

    const fileRef = useRef(null);

    const load = async () => {
        try {
            const { data } = await api.get("/admin/backup/status");
            setEnabled(!!data.enabled);
            setFrequency(data.frequency || "weekly");
            setDay(data.day || "sunday");
            setTime(data.time || "23:59");
            setEmail(data.email || "");
            setLastAt(data.last_at);
            setNextAt(data.next_at);
        } catch {/* swallow */}
        setLoaded(true);
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        setBusy(true);
        try {
            await api.put("/admin/settings", {
                backup_enabled: !!enabled,
                backup_frequency: frequency,
                backup_day: day,
                backup_time: time,
                backup_email: email || "",
            });
            toast.success("Backup schedule saved");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not save backup settings");
        }
        setBusy(false);
    };

    const downloadNow = async () => {
        setRunning(true);
        try {
            const res = await api.post("/admin/backup/run-now", null, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `evoteverify-backup-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.json`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success("Backup downloaded");
        } catch {
            toast.error("Could not generate backup");
        }
        setRunning(false);
    };

    const sendNow = async () => {
        if (!email) { toast.error("Set 'Send backup to' email first"); return; }
        setSending(true);
        try {
            await api.post("/admin/backup/send-now");
            toast.success(`Backup emailed to ${email}`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not email backup");
        }
        setSending(false);
    };

    const onUpload = async (ev) => {
        const file = ev.target.files?.[0];
        if (!file) return;
        if (!window.confirm("This will REPLACE all voters, ballots, blockchain, and settings with the contents of this file. Continue?")) {
            ev.target.value = "";
            return;
        }
        setRestoring(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/backup/restore", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const counts = data?.restored || {};
            const summary = Object.entries(counts).map(([k, v]) => `${k}: ${v}`).join(" · ");
            toast.success(`Restored — ${summary}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Restore failed");
        }
        setRestoring(false);
        ev.target.value = "";
    };

    const days = frequency === "monthly"
        ? Array.from({ length: 28 }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }))
        : [
            { value: "monday", label: "Monday" },
            { value: "tuesday", label: "Tuesday" },
            { value: "wednesday", label: "Wednesday" },
            { value: "thursday", label: "Thursday" },
            { value: "friday", label: "Friday" },
            { value: "saturday", label: "Saturday" },
            { value: "sunday", label: "Sunday" },
        ];

    const scheduleSummary = (() => {
        if (!enabled) return "Off";
        if (frequency === "daily") return `Daily at ${time} UTC`;
        if (frequency === "monthly") return `Monthly on day ${day} at ${time} UTC`;
        const dn = day.charAt(0).toUpperCase() + day.slice(1);
        return `Weekly on ${dn} at ${time} UTC`;
    })();

    return (
        <Card className="bg-white border-slate-200 overflow-hidden" data-testid="backup-panel">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="w-full flex items-center justify-between gap-3 px-5 py-4 hover:bg-slate-50 transition-colors"
                aria-expanded={open}
                data-testid="backup-panel-toggle"
            >
                <div className="flex items-center gap-3 min-w-0">
                    <div className="h-9 w-9 grid place-items-center rounded-md bg-civic-50 border border-civic-100">
                        <Database size={18} weight="duotone" className="text-civic-700" />
                    </div>
                    <div className="text-left min-w-0">
                        <h3 className="font-heading font-bold text-base text-slate-900">Backup &amp; Restore</h3>
                        <p className="text-xs text-slate-500 truncate">{scheduleSummary} {lastAt ? <span className="ml-2 text-slate-400">· last {fmtIso(lastAt)}</span> : null}</p>
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    {enabled ? (
                        <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 hash-mono text-[10px]">Active</Badge>
                    ) : (
                        <Badge className="bg-slate-100 text-slate-600 border border-slate-200 hash-mono text-[10px]">Off</Badge>
                    )}
                    {open ? <CaretUp size={18} weight="bold" className="text-slate-500" /> : <CaretDown size={18} weight="bold" className="text-slate-500" />}
                </div>
            </button>

            {open && loaded && (
                <div className="px-5 pb-5 pt-1 border-t border-slate-100" data-testid="backup-panel-body">
                    {/* Status banner */}
                    <div className={`mt-4 rounded-lg border px-4 py-3 ${enabled ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`} data-testid="backup-status-banner">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="flex items-start gap-2 min-w-0">
                                <CheckCircle size={20} weight="fill" className={enabled ? "text-emerald-600 mt-0.5" : "text-slate-400 mt-0.5"} />
                                <div>
                                    <div className={`font-semibold text-sm ${enabled ? "text-emerald-900" : "text-slate-700"}`}>
                                        {enabled ? "Automated Backups Active" : "Automated Backups Disabled"}
                                    </div>
                                    <div className="mt-1.5 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-[12px] text-slate-700">
                                        <div className="flex items-center gap-1.5"><Clock size={13} weight="bold" className="text-slate-500" /> <span className="text-slate-500">Last backup:</span> <span className="font-semibold">{fmtIso(lastAt)}</span></div>
                                        <div className="flex items-center gap-1.5"><Database size={13} weight="bold" className="text-slate-500" /> <span className="text-slate-500">Type:</span> <span className="font-semibold">{enabled ? "Scheduled" : "Manual only"}</span></div>
                                        <div className="flex items-center gap-1.5"><Calendar size={13} weight="bold" className="text-slate-500" /> <span className="text-slate-500">Next backup:</span> <span className="font-semibold">{enabled ? fmtIso(nextAt) : "—"}</span></div>
                                        <div className="flex items-center gap-1.5"><EnvelopeSimple size={13} weight="bold" className="text-slate-500" /> <span className="text-slate-500">Email to:</span> <span className="font-semibold hash-mono">{email || "—"}</span></div>
                                    </div>
                                </div>
                            </div>
                            <Badge className={`${enabled ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-slate-200 text-slate-700 border-slate-300"} text-[10px] hash-mono shrink-0 self-start`}>
                                {scheduleSummary}
                            </Badge>
                        </div>
                    </div>

                    {/* Schedule settings */}
                    <div className="mt-5 rounded-lg bg-slate-50 border border-slate-200 px-4 py-4">
                        <h4 className="font-heading font-bold text-sm text-slate-900">Automatic Backup Settings</h4>
                        <label className="mt-3 flex items-center gap-2 text-sm text-slate-800 select-none">
                            <Checkbox checked={enabled} onCheckedChange={(v) => setEnabled(!!v)} data-testid="backup-enabled" />
                            Enable Automatic Backups
                        </label>

                        <div className="mt-4 flex flex-wrap items-end gap-4">
                            <div>
                                <Label className="overline text-slate-500 text-[10px]">Frequency</Label>
                                <Select value={frequency} onValueChange={(v) => { setFrequency(v); if (v === "monthly") setDay("1"); else if (v === "weekly") setDay("sunday"); }}>
                                    <SelectTrigger className="mt-1 h-9 w-32 text-sm" data-testid="backup-frequency"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="daily">Daily</SelectItem>
                                        <SelectItem value="weekly">Weekly</SelectItem>
                                        <SelectItem value="monthly">Monthly</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            {frequency !== "daily" && (
                                <div>
                                    <Label className="overline text-slate-500 text-[10px]">{frequency === "monthly" ? "Day of month" : "Day"}</Label>
                                    <Select value={day} onValueChange={setDay}>
                                        <SelectTrigger className="mt-1 h-9 w-36 text-sm" data-testid="backup-day"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {days.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}
                            <div>
                                <Label className="overline text-slate-500 text-[10px]">Time (UTC)</Label>
                                <Input
                                    type="time"
                                    value={time}
                                    onChange={(e) => setTime(e.target.value)}
                                    className="mt-1 h-9 w-32 text-sm font-mono"
                                    data-testid="backup-time"
                                />
                            </div>
                        </div>

                        <div className="mt-4 grid sm:grid-cols-[1fr_auto] gap-2 items-end">
                            <div>
                                <Label className="overline text-slate-500 text-[10px]">Send backup to</Label>
                                <Input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="ops@yourdomain.com"
                                    className="mt-1 h-10 text-sm"
                                    data-testid="backup-email"
                                />
                            </div>
                            <Button type="button" variant="outline" onClick={sendNow} disabled={sending || !email} className="h-10" data-testid="backup-send-now">
                                <PaperPlaneTilt size={14} className="mr-1.5" weight="bold" />
                                {sending ? "Sending…" : "Send Now"}
                            </Button>
                        </div>

                        <div className="mt-5 flex justify-end">
                            <Button type="button" onClick={save} disabled={busy} className="bg-civic-600 hover:bg-civic-700 text-white" data-testid="backup-save">
                                <FloppyDisk size={14} className="mr-1.5" weight="bold" />
                                {busy ? "Saving…" : "Save Settings"}
                            </Button>
                        </div>
                    </div>

                    {/* Manual backup */}
                    <div className="mt-5 flex items-center justify-between gap-3 flex-wrap">
                        <div>
                            <h4 className="font-heading font-bold text-sm text-slate-900">Manual Backup</h4>
                            <p className="text-xs text-slate-500">Full snapshot: admins, voters, elections, blockchain, vote log, site settings &amp; footer, email history</p>
                        </div>
                        <Button type="button" onClick={downloadNow} disabled={running} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="backup-download-now">
                            <Database size={14} className="mr-1.5" weight="fill" />
                            {running ? "Generating…" : "Create Backup Now"}
                        </Button>
                    </div>

                    {/* Restore */}
                    <div className="mt-4 rounded-lg bg-civic-50 border border-civic-100 px-4 py-4 flex items-center justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                            <h4 className="font-heading font-bold text-sm text-civic-800 flex items-center gap-1.5">
                                <UploadSimple size={15} weight="bold" /> Upload &amp; Restore from File
                            </h4>
                            <p className="text-xs text-civic-700">Restore from a previously downloaded JSON backup file. <span className="font-semibold">This replaces all current data.</span></p>
                        </div>
                        <input
                            ref={fileRef}
                            type="file"
                            accept="application/json,.json"
                            onChange={onUpload}
                            className="hidden"
                            data-testid="backup-restore-file"
                        />
                        <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={restoring} className="border-civic-300 text-civic-700 hover:bg-civic-100" data-testid="backup-restore-trigger">
                            <UploadSimple size={14} className="mr-1.5" weight="bold" />
                            {restoring ? "Restoring…" : "Upload Backup File"}
                        </Button>
                    </div>
                </div>
            )}
        </Card>
    );
}

function AddressValidationHint({ check, onUseMatch }) {
    if (!check) return null;

    if (check.loading) {
        return (
            <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-slate-500" data-testid="addr-check-loading">
                <CircleNotch size={12} className="animate-spin" />
                <span>Validating address…</span>
            </div>
        );
    }

    if (check.po_box) {
        return (
            <div className="mt-1.5 flex items-start gap-1.5 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5" data-testid="addr-check-pobox">
                <WarningCircle size={13} weight="fill" className="mt-0.5 shrink-0" />
                <span>{check.message}</span>
            </div>
        );
    }

    if (!check.valid) {
        return (
            <div className="mt-1.5 flex items-start gap-1.5 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid="addr-check-unmatched">
                <Warning size={13} weight="fill" className="mt-0.5 shrink-0" />
                <span>{check.message}</span>
            </div>
        );
    }

    // valid match
    const tone = check.in_target_county
        ? "text-emerald-800 bg-emerald-50 border-emerald-200"
        : "text-amber-800 bg-amber-50 border-amber-200";
    const Icon = check.in_target_county ? CheckCircle : Warning;

    return (
        <div className={`mt-1.5 text-[11px] border rounded px-2 py-1.5 ${tone}`} data-testid="addr-check-valid">
            <div className="flex items-start gap-1.5">
                <Icon size={13} weight="fill" className="mt-0.5 shrink-0" />
                <div className="min-w-0">
                    <div className="font-semibold">{check.message}</div>
                    <div className="mt-0.5 text-slate-600 hash-mono text-[10px] truncate" title={check.matched_address}>
                        {check.matched_address}
                    </div>
                    {check.matched_address && onUseMatch && (
                        <button
                            type="button"
                            onClick={() => onUseMatch(check.matched_address)}
                            className="mt-1 text-[10px] underline underline-offset-2 text-civic-700 hover:text-civic-900"
                            data-testid="addr-check-use-match"
                        >
                            Use matched format
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}


